import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';

// DEBUG: Component loaded
// Removed console.log for production
import { 
  ArrowLeft, 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Users, 
  Calendar,
  Globe,
  Tag,
  User,
  FileText,
  Edit,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  Settings,
  Save,
  Plus,
  Minus,
  Search,
  Star
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import FavoriteButton from '../components/FavoriteButton';
import EditModal from '../components/EditModal';
import ImageManager from '../components/ImageManager';
import AmazonProductsManager from '../components/AmazonProductsManager';
import AmazonProductCard from '../components/AmazonProductCard';
import SavePreviewModal from '../components/SavePreviewModal';
import IconSearchModal from '../components/IconSearchModal';
import MarkdownEditor from '../components/MarkdownEditor';
import RelatedLinksModal from '../components/RelatedLinksModal';
import ImageCropUploader from '../components/ImageCropUploader';
import { formatSegmentLabel, normalizeStatisticSegment } from '@shared/statistic-segment';
import { normalizeRelatedLinks } from '@shared/related-links';
import { withMathMarkdown } from '@shared/math-markdown';

interface StatisticBlock {
  id: string;
  order?: number; // Para preservar el orden de los bloques
  title: string;
  value: number;
  unit?: 'PERCENTAGE' | 'ADDITION' | 'SUBTRACTION' | 'MULTIPLICATION' | 'DIVISION' | string;
  source?: string;
  source_url?: string;
  type: 'extracted' | 'estimation' | 'calculated' | 'assumption';
  description: string;
  formula?: string;
  image_icon?: string; // URL del icono del bloque
  hidden?: boolean; // Si true, no aparece en Análisis Detallado pero sí como {{fact:...}} en el artículo
}

interface StatisticSegment {
  target_block: string;
  dimension: string;
  dimension_title?: string;
  description: string;
  values: Record<string, number>;
}

interface StatisticChart {
  id: string;
  target_block: string;
  dimension: string;
  dimension_title?: string;
  description: string;
  values: Record<string, number>;
  valuesOrder?: string[]; // Array to preserve the order of keys
  icons?: Record<string, string>; // Icon URLs for each key
  orientation?: 'vertical' | 'horizontal' | 'radial';
  xAxisTitle?: string;
  yAxisTitle?: string;
  unit?: string; // Unit to display after values (e.g., "%", "€", "kg")
}

interface StatisticEvolution {
  target_block: string;
  years: number[];
  values: Array<{
    year: number;
    value: number;
    segments: Record<string, Record<string, number>>;
    description: string;
  }>;
}

interface Statistic {
  id: string;
  title: string;
  lead?: string;
  description: string;
  location: string;
  language: string;
  status: 'draft' | 'pending' | 'published';
  author: string;
  authorId?: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  slug: string;
  imageId?: string;
  studyFlow: string;
  category?: 'politica' | 'sociedad' | 'economia' | 'cultura' | 'entretenimiento' | 'deportes' | 'tecnologia' | 'ciencia' | 'lifestyle' | 'inclasificables';
  highlights?: string[];
  conclusion?: string;
  radar?: string;
  relatedLinks?: { title: string; slug: string }[];
  relatedAdProducts?: string[]; // Array de IDs de productos Amazon relacionados
  featured?: boolean;
  // SEO Fields
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string[];
  ogImage?: string;
  ogImageAlt?: string;
  canonicalUrl?: string;
  data: {
    blocks: StatisticBlock[];
    segments: StatisticSegment[];
    evolution?: StatisticEvolution;
    charts?: StatisticChart[]; // Added charts field
  };
}

type ChartType = 'bar' | 'pie' | 'radial' | 'line' | 'area';

const StatisticView: React.FC = () => {
  // console.log('🚀 DEBUG: StatisticView component function called'); // Removed for production
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [statistic, setStatistic] = useState<Statistic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [selectedChart, setSelectedChart] = useState<string>('');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedStatistic, setEditedStatistic] = useState<Statistic | null>(null);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    type: 'text' | 'block' | 'segment' | 'chart' | 'tags' | 'seo' | 'markdown' | 'highlights' | 'related-links';
    title: string;
    data: any;
  } | null>(null);
  const [showSavePreview, setShowSavePreview] = useState(false);
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [tempImageData, setTempImageData] = useState<{ id?: string; url: string; data?: string; metadata: any } | null>(null);
  const [svgIconModal, setSvgIconModal] = useState<{isOpen: boolean, selectedKey: string | null, type: 'chart' | 'block'}>({isOpen: false, selectedKey: null, type: 'chart'});
  const [tempChartValues, setTempChartValues] = useState<Record<string, number>>({});
  const [editingKeys, setEditingKeys] = useState<Record<string, string>>({});
  const [chartKeysOrder, setChartKeysOrder] = useState<string[]>([]);
  const [amazonProducts, setAmazonProducts] = useState<Array<{ id: string; asin: string; title: string; price?: string; imageUrl?: string }>>([]);
  const [experiences, setExperiences] = useState<Array<{ id: string; title: string; description?: string; type: 'game' | 'calculator' | 'visualization' | 'quiz' | 'simulator' | 'other' }>>([]);
  const [metrics, setMetrics] = useState<{
    favoritesCount: number;
    sharesCount: number;
    viewsCount: number;
  } | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showOgImageUploader, setShowOgImageUploader] = useState(false);
  const [authors, setAuthors] = useState<Array<{ id: string; name: string; isDefault: boolean }>>([]);
  const { t } = useTranslation();

  // Get statistic ID from URL
  const pathParts = window.location.pathname.split('/');
  const statisticId = pathParts[pathParts.length - 1];

  useEffect(() => {
    // console.log('🚀 DEBUG: StatisticView component mounted, fetching statistic with ID:', statisticId); // Removed for production
    fetchStatistic();
    fetchAmazonProducts();
    fetchExperiences();
  }, [statisticId]);

  // Re-cargar productos Amazon cuando cambien los productos relacionados
  useEffect(() => {
    if (editedStatistic?.relatedAdProducts !== undefined) {
      // console.log('🛒 STATISTIC VIEW: relatedAdProducts changed, reloading Amazon products'); // Removed for production
      fetchAmazonProducts();
    }
  }, [editedStatistic?.relatedAdProducts]);

  useEffect(() => {
    if (statistic?.id) {
      fetchMetrics();
      checkFavoriteStatus();
    }
  }, [statistic?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/authors', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAuthors(Array.isArray(data) ? data : []);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close source tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.tooltip-source') && !target.closest('button[onclick*="tooltip"]')) {
        document.querySelectorAll('.tooltip-source').forEach(tooltip => {
          tooltip.classList.add('opacity-0');
          tooltip.classList.remove('opacity-100');
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const checkFavoriteStatus = async () => {
    if (!user || !statistic?.id) return;
    
    try {
      const response = await fetch(`/api/favorites/check/${statistic.id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.isFavorited);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const fetchMetrics = async () => {
    if (!statistic?.id) return;
    
    try {
      const response = await fetch(`/api/statistics/${statistic.id}/metrics`);
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const fetchStatistic = async () => {
    try {
      // console.log('🔍 DEBUG: fetchStatistic called for ID:', statisticId); // Removed for production
      setLoading(true);
      const response = await fetch(`/api/statistics/${statisticId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch statistic');
      }
      
      const data = await response.json();
      // console.log('🔍 DEBUG: Received data from API:', data); // Removed for production
      // console.log('🔍 DEBUG: Radar field in received data:', data.radar); // Removed for production
      console.log('🛒 DEBUG: relatedAdProducts from API:', data.relatedAdProducts);
      console.log('🛒 DEBUG: relatedAdProducts type:', typeof data.relatedAdProducts);
      console.log('🛒 DEBUG: relatedAdProducts isArray:', Array.isArray(data.relatedAdProducts));
      
      // Asegurarse de que relatedAdProducts sea un array
      if (data.relatedAdProducts && typeof data.relatedAdProducts === 'string') {
        try {
          data.relatedAdProducts = JSON.parse(data.relatedAdProducts);
          console.log('🛒 DEBUG: Parsed relatedAdProducts from string:', data.relatedAdProducts);
        } catch (e) {
          console.error('🛒 DEBUG: Error parsing relatedAdProducts:', e);
          data.relatedAdProducts = [];
        }
      }
      
      // Si es null o undefined, convertir a array vacío
      if (!data.relatedAdProducts) {
        data.relatedAdProducts = [];
      }
      
      // Clean entire statistic to remove any nested structures
      const cleanedData = cleanStatisticData(data);
      console.log('🛒 DEBUG: relatedAdProducts after clean:', cleanedData.relatedAdProducts);
      
      // Normalize block order - assign order to blocks that don't have it
      if (cleanedData.data.blocks) {
        const blocks = cleanedData.data.blocks;
        
        // First, assign order to blocks that don't have it
        let nextOrder = 1;
        blocks.forEach(block => {
          if (block.order === undefined) {
            block.order = nextOrder++;
          }
        });
        
        // Then sort by order
        blocks.sort((a, b) => a.order! - b.order!);
      }
      
      // console.log('🔍 DEBUG: Loaded statistic radar field:', cleanedData.radar); // Removed for production
      // console.log('🔍 DEBUG: Full cleanedData object keys:', Object.keys(cleanedData)); // Removed for production
      setStatistic(cleanedData);
      setEditedStatistic(cleanedData);
      
      // Set initial selected segment
      if (cleanedData.data?.segments?.length > 0) {
        setSelectedSegment(cleanedData.data.segments[0].dimension);
      }
      
      // Set initial selected chart
      if (data.data?.charts?.length > 0) {
        setSelectedChart(data.data.charts[0].id);
      }
      
      // Set initial selected year for evolution (if it exists)
      if (data.data.evolution && data.data.evolution.years && data.data.evolution.years.length > 0) {
        setSelectedYear(data.data.evolution.years[data.data.evolution.years.length - 1]);
      }
    } catch (err) {
      console.error('❌ DEBUG: Error in fetchStatistic:', err);
      console.error('❌ DEBUG: Error message:', err instanceof Error ? err.message : 'Unknown error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAmazonProducts = async () => {
    try {
      // Cargar solo los productos relacionados con este artículo
      // Los IDs están en editedStatistic.relatedAdProducts
      console.log('🛒 DEBUG: fetchAmazonProducts called');
      console.log('🛒 DEBUG: editedStatistic?.relatedAdProducts:', editedStatistic?.relatedAdProducts);
      console.log('🛒 DEBUG: relatedAdProducts type:', typeof editedStatistic?.relatedAdProducts);
      console.log('🛒 DEBUG: relatedAdProducts isArray:', Array.isArray(editedStatistic?.relatedAdProducts));
      
      if (editedStatistic?.relatedAdProducts && editedStatistic.relatedAdProducts.length > 0) {
        console.log('🛒 DEBUG: Fetching products with IDs:', editedStatistic.relatedAdProducts);
        
        const response = await fetch('/api/amazon/products/by-ids', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ids: editedStatistic.relatedAdProducts
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('🛒 DEBUG: Products fetched:', data.products?.length || 0);
          setAmazonProducts(data.products || []);
        } else {
          console.error('🛒 DEBUG: Failed to fetch products, status:', response.status);
          const errorText = await response.text();
          console.error('🛒 DEBUG: Error response:', errorText);
          setAmazonProducts([]);
        }
      } else {
        console.log('🛒 DEBUG: No relatedAdProducts or empty array');
        setAmazonProducts([]);
      }
    } catch (error) {
      console.error('🛒 DEBUG: Error fetching Amazon products:', error);
      setAmazonProducts([]);
    }
  };

  const fetchAllAmazonProducts = async () => {
    try {
      // console.log('🛒 STATISTIC VIEW: Loading all active products for MarkdownEditor'); // Removed for production
      const response = await fetch('/api/amazon/products/active');
      if (response.ok) {
        const data = await response.json();
        setAmazonProducts(data.products || []);
        // console.log('🛒 STATISTIC VIEW: Loaded all products for MarkdownEditor:', data.products?.length || 0); // Removed for production
      }
    } catch (error) {
      console.error('Error fetching all Amazon products:', error);
    }
  };

  const fetchExperiences = async () => {
    try {
      const response = await fetch('/api/experiences?active=true');
      if (response.ok) {
        const data = await response.json();
        setExperiences(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching experiences:', error);
    }
  };

  const handleEditField = (field: string) => {
    setEditingField(field);
  };

  const handleSaveField = (field: string, value: any) => {
    if (!editedStatistic) return;
    
    // console.log('🔍 DEBUG: handleSaveField called with field:', field, 'value:', value); // Removed for production
    if (field === 'radar') {
      // console.log('🔍 DEBUG: Specifically updating radar field with value:', value); // Removed for production
    }
    
    if (field === 'imageId') {
      // console.log('🔍 DEBUG: handleSaveField imageId called with:', value); // Removed for production
      // Handle image data (could be temporary or permanent)
      if (typeof value === 'object' && value.url) {
        // This is temporary image data
        // console.log('🔍 DEBUG: Setting temporary image data:', value); // Removed for production
        setTempImageData(value);
        
        // Update both imageId and ogImage with the temporary data
        setEditedStatistic({
          ...editedStatistic,
          imageId: value.id, // Use the temporary ID for now
          ogImage: value.url, // Set ogImage to the temporary URL
          ogImageAlt: value.metadata?.alt || '' // Set ogImageAlt from metadata
        });
      } else {
        // This is a permanent image ID
        // console.log('🔍 DEBUG: Setting permanent image ID:', value); // Removed for production
        setTempImageData(null);
        
        // For permanent images, we need to fetch the metadata to get the correct URL
        if (value) {
          fetch(`/api/images/${value}/metadata`)
            .then(response => response.json())
            .then(imageData => {
              let imageUrl;
              if (imageData.metadata?.source === 'upload') {
                // Imagen nuestra - usar URL local
                const baseUrl = window.location.origin;
                imageUrl = `${baseUrl}/api/images/serve/${value}`;
              } else {
                // Imagen de stock (Freepik/Pexels) - usar URL externa
                imageUrl = imageData.url;
              }
              
              setEditedStatistic(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  imageId: value,
                  ogImage: imageUrl,
                                          ogImageAlt: generateImageAttribution(imageData.metadata, imageData.url)
                };
              });
            })
            .catch(error => {
              console.error('Error fetching image metadata:', error);
              // Fallback: just update imageId
              setEditedStatistic({
                ...editedStatistic,
                imageId: value
              });
            });
        } else {
          // No image selected
          setEditedStatistic({
            ...editedStatistic,
            imageId: value,
            ogImage: '', // Clear ogImage
            ogImageAlt: '' // Clear ogImageAlt
          });
        }
      }
    } else {
      setEditedStatistic({
        ...editedStatistic,
        [field]: value
      });
    }
    setEditingField(null);
  };

  const handleSaveBlock = (blockId: string, field: string, value: any) => {
    if (!editedStatistic) return;
    
    setEditedStatistic({
      ...editedStatistic,
      data: {
        ...editedStatistic.data,
        blocks: editedStatistic.data?.blocks?.map(block => 
          block.id === blockId ? { ...block, [field]: value } : block
        ) || []
      }
    });
  };

  const handleSaveSegment = (dimension: string, field: string, value: any) => {
    if (!editedStatistic) return;
    
    setEditedStatistic({
      ...editedStatistic,
      data: {
        ...editedStatistic.data,
        segments: editedStatistic.data?.segments?.map(segment => 
          segment.dimension === dimension ? { ...segment, [field]: value } : segment
        ) || []
      }
    });
  };

  const handleOpenModal = (type: 'text' | 'block' | 'segment' | 'chart' | 'tags' | 'seo' | 'markdown' | 'highlights' | 'related-links', title: string, data: any) => {
    // Initialize temporary chart values if opening a chart modal
    if (type === 'chart' && data.chart) {
      // Check if this is a new chart or editing existing one
      if (data.isNew) {
        // New chart - start empty
        setTempChartValues({});
        setChartKeysOrder([]);
      } else {
        // Existing chart - load current values
        const existingValues = data.chart.values || {};
        setTempChartValues(existingValues);
        // Use preserved order if available, otherwise use Object.keys
        const existingOrder = data.chart.valuesOrder || Object.keys(existingValues);
        setChartKeysOrder(existingOrder);
      }
      setEditingKeys({});
    }
    
    setModalConfig({
      isOpen: true,
      type,
      title,
      data: type === 'related-links'
        ? { ...data, value: normalizeRelatedLinks(data.value) }
        : data
    });
  };

  // Function to clean block data and remove nested structures
  const cleanBlockData = (block: any): StatisticBlock => {
    return {
      id: block.id,
      order: block.order,
      type: block.type,
      title: block.title,
      value: block.value,
      unit: block.unit,
      source: block.source,
      source_url: block.source_url,
      description: block.description,
      formula: block.formula,
      image_icon: block.image_icon,
      hidden: block.hidden === true,
    };
  };

  // Function to clean segment data and remove nested structures
  const cleanSegmentData = (segment: any, index: number): StatisticSegment => {
    return normalizeStatisticSegment(segment, index);
  };

  // Function to clean entire statistic data
  const cleanStatisticData = (statistic: any): Statistic => {
    return {
      ...statistic,
      relatedLinks: normalizeRelatedLinks(statistic.relatedLinks),
      data: {
        ...statistic.data,
        blocks: statistic.data?.blocks?.map(cleanBlockData) || [],
        segments: statistic.data?.segments?.map((segment, index) => cleanSegmentData(segment, index)) || [],
        charts: statistic.data?.charts?.map(cleanChartData) || [] // Clean charts
      }
    };
  };

  // Function to clean chart data and remove nested structures
  const cleanChartData = (chart: any): StatisticChart => {
    return {
      id: chart.id,
      target_block: chart.target_block,
      dimension: chart.dimension,
      dimension_title: chart.dimension_title,
      description: chart.description,
      values: chart.values,
      valuesOrder: chart.valuesOrder || Object.keys(chart.values || {}),
      icons: chart.icons || {},
      orientation: chart.orientation || 'vertical',
      xAxisTitle: chart.xAxisTitle || 'Eje X',
      yAxisTitle: chart.yAxisTitle || 'Eje Y',
      unit: chart.unit || ''
    };
  };

  // Function to get the next available block ID
  const getNextAvailableBlockId = (): string => {
    if (!editedStatistic?.data.blocks) return 'P1';
    
    const existingIds = editedStatistic.data?.blocks?.map(block => block.id) || [];
    let nextNumber = 1;
    
    while (existingIds.includes(`P${nextNumber}`)) {
      nextNumber++;
    }
    
    return `P${nextNumber}`;
  };

  // Function to get the next available chart ID
  const getNextAvailableChartId = (): string => {
    if (!editedStatistic?.data.charts) return 'C1';
    
    const existingIds = editedStatistic.data?.charts?.map(chart => chart.id) || [];
    let nextNumber = 1;
    
    while (existingIds.includes(`C${nextNumber}`)) {
      nextNumber++;
    }
    
    return `C${nextNumber}`;
  };

  // Function to handle adding a new chart
  const handleAddChart = () => {
    const nextId = getNextAvailableChartId();
    
    const newChart: StatisticChart = {
      id: nextId,
      target_block: '',
      dimension: '',
      dimension_title: '',
      description: 'Descripción del nuevo gráfico',
      values: {},
      icons: {},
      orientation: 'vertical',
      xAxisTitle: 'Eje X',
      yAxisTitle: 'Eje Y'
    };
    
    // console.log('➕ CREATING NEW CHART:', {
      // nextId,
      // newChart,
      // currentCharts: editedStatistic?.data?.charts?.length || 0
    // }); // Removed for production
    
    // Initialize temporary values state for new chart
    setTempChartValues({});
    setChartKeysOrder([]);
    
    // Open the modal to edit the new chart (don't add to state yet)
    handleOpenModal('chart', `Crear Nuevo Gráfico: ${newChart.id}`, {
      chartId: newChart.id,
      chart: newChart,
      isNew: true
    });
  };

  // Function to handle adding a new block
  const handleAddBlock = (insertIndex: number) => {
    const nextId = getNextAvailableBlockId();
    
    const newBlock: StatisticBlock = {
      id: nextId,
      order: insertIndex + 1,
      type: 'extracted',
      title: 'Nuevo Bloque',
      value: 0,
      description: 'Descripción del nuevo bloque',
      source: '',
      source_url: ''
    };
    
    // Insert the new block at the specified position
    const updatedBlocks = [...(editedStatistic?.data.blocks || [])];
    updatedBlocks.splice(insertIndex, 0, newBlock);
    
    // Update the order of all blocks after the insertion point
    updatedBlocks.forEach((block, index) => {
      if (index >= insertIndex) {
        block.order = index + 1;
      }
    });
    
    // Update the state
    setEditedStatistic(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          blocks: updatedBlocks
        }
      };
    });
    
    // Open the modal to edit the new block
    handleOpenModal('block', `Crear Nuevo Bloque: ${newBlock.id}`, {
      blockId: newBlock.id,
      block: newBlock
    });
  };

  const handleCloseModal = () => {
    setModalConfig(null);
    setTempChartValues({});
    setEditingKeys({});
    setChartKeysOrder([]);
  };

  const handleOpenSvgIconModal = (key: string, type: 'chart' | 'block' = 'chart') => {
    setSvgIconModal({isOpen: true, selectedKey: key, type});
  };

  const handleCloseSvgIconModal = () => {
    setSvgIconModal({isOpen: false, selectedKey: null, type: 'chart'});
  };

  const handleSelectSvgIcon = (icon: any) => {
    if (!svgIconModal.selectedKey || !modalConfig) return;
    
    if (svgIconModal.type === 'chart') {
      // Update the icons in the modal config for charts
      const newIcons = { ...modalConfig.data.chart.icons };
      newIcons[svgIconModal.selectedKey] = icon.url;
      
      modalConfig.data.chart.icons = newIcons;
      setModalConfig({...modalConfig});
    } else if (svgIconModal.type === 'block') {
      // Update the icon in the modal config for blocks
      modalConfig.data.block.image_icon = icon.url;
      setModalConfig({...modalConfig});
    }
    
    handleCloseSvgIconModal();
  };

  const handleModalSave = (data: any) => {
    if (!modalConfig) return;

    switch (modalConfig.type) {
      case 'text':
        handleSaveField(modalConfig.data.field, data);
        break;
      case 'markdown':
        // console.log('🔍 DEBUG: Saving markdown field:', modalConfig.data.field, 'with data:', data); // Removed for production
        if (modalConfig.data.field === 'radar') {
          // console.log('🔍 DEBUG: Specifically saving radar field with value:', data); // Removed for production
        }
        handleSaveField(modalConfig.data.field, data);
        break;
      case 'block':
        if (modalConfig.data.blockId && modalConfig.data.block) {
          const blockData = modalConfig.data.block;

          let processedValue = blockData.value;
          if (typeof blockData.value === 'string') {
            const parsed = parseFloat(blockData.value);
            processedValue = isNaN(parsed) ? 0 : parsed;
          }

          const processedData: StatisticBlock = {
            ...cleanBlockData(blockData),
            value: processedValue ?? 0,
            hidden: blockData.hidden === true,
          };

          setEditedStatistic(prev => {
            if (!prev) return prev;
            
            // Update the modified block
            const updatedBlocks = prev.data.blocks.map(block => 
              block.id === modalConfig.data.blockId ? processedData : block
            );
            
            // Apply cascade calculations if the changed block is of type 'extracted', 'estimation', or 'assumption'
            // Also trigger cascade if the unit/operation changed for estimation/assumption blocks
            const currentBlock = prev.data.blocks.find(b => b.id === modalConfig.data.blockId);
            const unitChanged = currentBlock && currentBlock.unit !== processedData.unit;
            
            // console.log('=== CASCADE DEBUG ==='); // Removed for production
            // console.log('Current block unit:', currentBlock?.unit); // Removed for production
            // console.log('Processed data unit:', processedData.unit); // Removed for production
            // console.log('Unit changed:', unitChanged); // Removed for production
            // console.log('Block type:', processedData.type); // Removed for production
            
            const shouldUpdateCascade = (processedData.type === 'extracted' || processedData.type === 'estimation' || processedData.type === 'assumption') ||
              ((processedData.type === 'estimation' || processedData.type === 'assumption') && unitChanged);
            
            // console.log('Should update cascade:', shouldUpdateCascade); // Removed for production
            // console.log('===================='); // Removed for production
            
            const finalBlocks = shouldUpdateCascade
              ? updateCascade(modalConfig.data.blockId, updatedBlocks)
              : updatedBlocks;
            
            // Clean all blocks to remove any nested structures
            const cleanedBlocks = finalBlocks.map(cleanBlockData);
            
            return {
              ...prev,
              data: {
                ...prev.data,
                blocks: cleanedBlocks
              }
            };
          });
        }
        break;
      case 'segment':
        // For segment editing, data is the entire segment object
        if (modalConfig.data.dimension) {
          setEditedStatistic(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              data: {
                ...prev.data,
                segments: prev.data?.segments?.map(segment => 
                  segment.dimension === modalConfig.data.dimension ? data : segment
                ) || []
              }
            };
          });
        }
        break;
      case 'chart':
        // For chart editing, data is the entire chart object
        // Use temporary values if available, otherwise use data from modal
        const finalValues = Object.keys(tempChartValues).length > 0 ? tempChartValues : data.values;
        const finalChartData = {
          ...data,
          values: finalValues,
          valuesOrder: chartKeysOrder.length > 0 ? chartKeysOrder : Object.keys(finalValues)
        };
        
        // console.log('💾 SAVING CHART DATA:', {
          // chartId: modalConfig.data.chartId,
          // chartData: finalChartData,
          // isNew: modalConfig.data.isNew,
          // tempValues: tempChartValues,
          // valuesOrder: finalChartData.valuesOrder
        // }); // Removed for production
        
        if (modalConfig.data.chartId) {
          setEditedStatistic(prev => {
            if (!prev) return prev;
            
            // The data passed is the chart object from the modal
            const chartData = data;
            
            // Check if this is a new chart or editing existing one
            const existingChart = prev.data?.charts?.find(chart => chart.id === modalConfig.data.chartId);
            
            // console.log('📊 CHART SAVE OPERATION:', {
              // existingChart: existingChart ? 'UPDATE' : 'NEW',
              // currentCharts: prev.data?.charts?.length || 0,
              // chartDataKeys: Object.keys(chartData),
              // chartValues: chartData.values
            // }); // Removed for production
            
            if (existingChart) {
              // Update existing chart
              const updatedState = {
                ...prev,
                data: {
                  ...prev.data,
                  charts: prev.data?.charts?.map(chart => 
                    chart.id === modalConfig.data.chartId ? finalChartData : chart
                  ) || []
                }
              };
              // console.log('🔄 UPDATED CHART STATE:', {
                // updatedCharts: updatedState.data.charts,
                // chartCount: updatedState.data.charts.length
              // }); // Removed for production
              return updatedState;
            } else {
              // Add new chart
              const updatedState = {
                ...prev,
                data: {
                  ...prev.data,
                  charts: [...(prev.data.charts || []), finalChartData]
                }
              };
              // console.log('➕ ADDED NEW CHART STATE:', {
                // updatedCharts: updatedState.data.charts,
                // chartCount: updatedState.data.charts.length
              // }); // Removed for production
              
              // Set the new chart as selected if it's the first one
              if (updatedState.data.charts.length === 1) {
                setSelectedChart(finalChartData.id);
              }
              
              return updatedState;
            }
          });
        }
        break;
      case 'tags':
        handleSaveField('tags', data.split(',').map((tag: string) => tag.trim()));
        break;
      case 'highlights':
        handleSaveField('highlights', data);
        break;
      case 'related-links':
        handleSaveField('relatedLinks', normalizeRelatedLinks(modalConfig.data.value));
        break;
      case 'seo':
        // Handle SEO fields
        setEditedStatistic(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            metaTitle: data.metaTitle,
            metaDescription: data.metaDescription,
            keywords: data.keywords.split(',').map((kw: string) => kw.trim()),
            ogImage: data.ogImage,
            ogImageAlt: data.ogImageAlt,
            canonicalUrl: data.canonicalUrl
          };
        });
        break;
    }
    
    handleCloseModal();
  };

  // Utility functions for dependency management and calculations
  const detectDependencies = (formula: string): string[] => {
    const matches = formula.match(/P\d+/g) || [];
    return Array.from(new Set(matches)); // Remove duplicates
  };

  const evaluateFormula = (formula: string, context: Record<string, number>): number => {
    // Replace P1, P2, etc. with actual values
    let safeFormula = formula;
    Object.entries(context).forEach(([blockId, value]) => {
      safeFormula = safeFormula.replace(new RegExp(blockId, 'g'), value.toString());
    });
    
    // Evaluate safely (only basic mathematical operations)
    try {
      return new Function('return ' + safeFormula)();
    } catch (error) {
      console.error('Error evaluating formula:', formula, error);
      return 0;
    }
  };

  const calculateBlockValue = (block: StatisticBlock, allBlocks: StatisticBlock[]): number => {
    if (!block.formula || block.type !== 'calculated') return block.value;
    
    // Create context with all blocks
    const context = allBlocks.reduce((acc, b) => {
      acc[b.id] = b.value;
      return acc;
    }, {} as Record<string, number>);
    
    // Evaluate formula safely
    return evaluateFormula(block.formula, context);
  };

  // Function to get operation symbol for display
  const getOperationSymbol = (unit: string): string => {
    switch (unit) {
      case 'PERCENTAGE': return '%';
      case 'ADDITION': return '+';
      case 'SUBTRACTION': return '-';
      case 'MULTIPLICATION': return '×';
      case 'DIVISION': return '÷';
      default: return unit;
    }
  };

  // Function to format unit display
  const formatUnitDisplay = (block: StatisticBlock): string => {
    if (!block.unit) return '';
    
    if (['PERCENTAGE', 'ADDITION', 'SUBTRACTION', 'MULTIPLICATION', 'DIVISION'].includes(block.unit)) {
      return `<span class="text-2xl font-light"> ${getOperationSymbol(block.unit)}</span>`;
    }
    
    return `<span class="text-2xl font-light">${block.unit}</span>`;
  };

  // Function to format text with line breaks for better readability
  const formatTextWithBreaks = (text: string): string => {
    if (!text) return '';
    
    // Split by sentences and add line breaks every 2-3 sentences
    const sentences = text.split(/(?<=[.!?])\s+/);
    const formattedSentences = [];
    
    for (let i = 0; i < sentences.length; i++) {
      formattedSentences.push(sentences[i]);
      
      // Add line break every 2-3 sentences (but not at the end)
      if ((i + 1) % 3 === 0 && i < sentences.length - 1) {
        formattedSentences.push('\n\n');
      }
    }
    
    return formattedSentences.join(' ');
  };

  // Function to generate automatic formula for estimation/assumption blocks
  const generateEstimationFormula = (estimationBlock: StatisticBlock, inputBlockId: string): string => {
    const operation = estimationBlock.unit;
    
    switch (operation) {
      case 'PERCENTAGE':
        return `${inputBlockId} * (${estimationBlock.id} / 100)`;
      case 'ADDITION':
        return `${inputBlockId} + ${estimationBlock.id}`;
      case 'SUBTRACTION':
        return `${inputBlockId} - ${estimationBlock.id}`;
      case 'MULTIPLICATION':
        return `${inputBlockId} * ${estimationBlock.id}`;
      case 'DIVISION':
        return `${inputBlockId} / ${estimationBlock.id}`;
      default:
        return estimationBlock.formula || '';
    }
  };

  // Function to get all blocks that depend on a given block (recursive)
  const getAllDependentBlocks = (blockId: string, allBlocks: StatisticBlock[]): string[] => {
    // console.log(`Looking for dependents of block ${blockId}`); // Removed for production
    // console.log('All blocks:', allBlocks.map(b => ({ id: b.id, type: b.type, formula: b.formula }))); // Removed for production
    
    const directDependents = allBlocks.filter(block => 
      block.type === 'calculated' && 
      block.formula && 
      detectDependencies(block.formula).includes(blockId)
    ).map(b => b.id);
    
    // console.log(`Direct dependents of ${blockId}:`, directDependents); // Removed for production
    
    const indirectDependents = directDependents.flatMap(depId => 
      getAllDependentBlocks(depId, allBlocks)
    );
    
    const result = Array.from(new Set([...directDependents, ...indirectDependents]));
    // console.log(`All dependents of ${blockId}:`, result); // Removed for production
    
    return result;
  };

  const updateCascade = (changedBlockId: string, allBlocks: StatisticBlock[]): StatisticBlock[] => {
    const updatedBlocks = [...allBlocks];
    const changedBlock = updatedBlocks.find(b => b.id === changedBlockId);
    
    // Get ALL dependent blocks (direct and indirect)
    const allDependentBlockIds = getAllDependentBlocks(changedBlockId, allBlocks);
    
    // console.log(`Block ${changedBlockId} changed. All dependent blocks:`, allDependentBlockIds); // Removed for production
    // console.log('All calculated blocks:', allBlocks.filter(b => b.type === 'calculated').map(b => ({ id: b.id, formula: b.formula }))); // Removed for production
    
    // Sort dependent blocks by dependency order (blocks with fewer dependencies first)
    const sortedDependentBlocks = allDependentBlockIds
      .map(id => allBlocks.find(b => b.id === id))
      .filter(Boolean)
      .sort((a, b) => {
        const aDeps = detectDependencies(a!.formula || '').length;
        const bDeps = detectDependencies(b!.formula || '').length;
        return aDeps - bDeps;
      });
    
    // console.log('Sorted dependent blocks by dependency order:', sortedDependentBlocks.map(b => b!.id)); // Removed for production
    
    // Update each dependent block in order
    sortedDependentBlocks.forEach(block => {
      let updatedBlock = { ...block! };
      
      // console.log(`Processing block ${block!.id}, changed block type: ${changedBlock?.type}, changed block unit: ${changedBlock?.unit}`); // Removed for production
      
      // Only update formulas if the changed block is an estimation/assumption block AND it's a direct dependency
      const isDirectDependency = detectDependencies(block!.formula || '').includes(changedBlockId);
      // console.log(`Is direct dependency: ${isDirectDependency}`); // Removed for production
      
      if (changedBlock && (changedBlock.type === 'estimation' || changedBlock.type === 'assumption') && isDirectDependency) {
        // Find the input block for this estimation/assumption (the block that this estimation/assumption operates on)
        const inputBlockId = detectDependencies(block!.formula || '').find(dep => dep !== changedBlockId);
        if (inputBlockId) {
          // Check if this is a simple estimation/assumption formula (just two blocks)
          const dependencies = detectDependencies(block!.formula || '');
          if (dependencies.length === 2) {
            // For estimation/assumption blocks with unit, generate new formula
            if ((changedBlock.type === 'estimation' || changedBlock.type === 'assumption') && changedBlock.unit) {
              const newFormula = generateEstimationFormula(changedBlock, inputBlockId);
              updatedBlock.formula = newFormula;
              // console.log(`Updated formula for ${block!.id}: ${block!.formula} → ${newFormula}`); // Removed for production
            } else {
              // For blocks without unit, just recalculate value
              // console.log(`Block without unit detected for ${block!.id}, preserving formula`); // Removed for production
            }
          } else {
            // Complex case: preserve the original formula structure
            // console.log(`Complex formula detected for ${block!.id}, preserving structure`); // Removed for production
          }
        }
      }
      
      // Calculate new value using the current formula (updated or original)
      const newValue = calculateBlockValue(updatedBlock, updatedBlocks);
      updatedBlock.value = newValue;
      
      const blockIndex = updatedBlocks.findIndex(b => b.id === block!.id);
      if (blockIndex !== -1) {
        // console.log(`Updating ${block!.id}: ${block!.value} → ${newValue}`); // Removed for production
        updatedBlocks[blockIndex] = updatedBlock;
      }
    });
    
    return updatedBlocks;
  };

  const handleAskAI = (field: string) => {
    // TODO: Implement AI assistance
    // console.log('Ask AI for:', field); // Removed for production
  };

  const handleSave = async () => {
    setShowSavePreview(true);
  };

        const handleSaveToDatabase = async () => {
        try {
          // Preparar datos para la tabla principal (excluyendo data)
                  const tableData = {
          location: editedStatistic?.location,
          language: editedStatistic?.language,
          slug: editedStatistic?.slug,
          title: editedStatistic?.title,
          lead: editedStatistic?.lead,
          description: editedStatistic?.description,
          studyFlow: editedStatistic?.studyFlow,
          tags: editedStatistic?.tags,
          imageId: editedStatistic?.imageId,
          author: editedStatistic?.author,
          authorId: editedStatistic?.authorId,
          status: editedStatistic?.status,
          category: editedStatistic?.category,
          highlights: editedStatistic?.highlights,
          conclusion: editedStatistic?.conclusion,
          radar: editedStatistic?.radar,
          relatedLinks: editedStatistic?.relatedLinks,
          relatedAdProducts: editedStatistic?.relatedAdProducts, // ¡Campo faltante!
          featured: editedStatistic?.featured,
          // SEO Fields
          metaTitle: editedStatistic?.metaTitle,
          metaDescription: editedStatistic?.metaDescription,
          keywords: editedStatistic?.keywords,
          ogImage: editedStatistic?.ogImage,
          ogImageAlt: editedStatistic?.ogImageAlt,
          canonicalUrl: editedStatistic?.canonicalUrl,
          data: editedStatistic?.data // Este campo va al JSONB
        };
        
        // console.log('🔍 DEBUG: Saving radar field:', editedStatistic?.radar); // Removed for production
        // console.log('🔍 AMAZON DEBUG: Saving relatedAdProducts:', editedStatistic?.relatedAdProducts); // Removed for production
        // console.log('🔍 DEBUG: Full tableData:', JSON.stringify(tableData, null, 2)); // Removed for production

          // console.log('🔍 DEBUG: Saving blocks with image_icons:', editedStatistic?.data?.blocks?.map(b => ({
            // id: b.id,
            // title: b.title,
            // image_icon: b.image_icon
          // }))); // Removed for production

      // If there's a new image, handle it appropriately
      // console.log('🔍 DEBUG: Checking for tempImageData:', tempImageData); // Removed for production
      let finalImageId = tableData.imageId;
      
      if (tempImageData && tempImageData.id?.startsWith('temp-')) {
        // console.log('🔍 DEBUG: Saving temporary image to database:', tempImageData); // Removed for production
        
        let skipUpload = false;
        
        // Check if this is a stock image that might already exist
        if (tempImageData.metadata?.source === 'stock') {
          // console.log('🔍 DEBUG: This is a stock image, checking if it already exists...'); // Removed for production
          
          // For stock images, check if the URL already exists in our database
          try {
            const existingImageResponse = await fetch(`/api/images/url/${encodeURIComponent(tempImageData.url)}`, {
              credentials: 'include'
            });
            
            if (existingImageResponse.ok) {
              const existingImage = await existingImageResponse.json();
              // console.log('🔍 DEBUG: Found existing image:', existingImage.id); // Removed for production
              
              // Use the existing image instead of creating a new one
              finalImageId = existingImage.id;
              tableData.imageId = existingImage.id;
              tableData.ogImage = tempImageData.url;
              tableData.ogImageAlt = generateImageAttribution(tempImageData.metadata, tempImageData.url);
              
              // Skip the upload process
              // console.log('🔍 DEBUG: Using existing image, skipping upload'); // Removed for production
              skipUpload = true;
            }
          } catch (error) {
            // console.log('🔍 DEBUG: No existing image found, proceeding with upload'); // Removed for production
          }
        }
        
        if (!skipUpload) {
          try {
          // Save temporary image to database
          const imageResponse = await fetch('/api/images/upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              filename: `statistic-${Date.now()}.jpg`,
              mimeType: 'image/jpeg',
              size: tempImageData.data ? tempImageData.data.length * 0.75 : 1024, // Use 1KB for stock images
              data: tempImageData.data || null, // Use null for stock images
              url: tempImageData.url,
              source: tempImageData.metadata.source || 'upload',
              metadata: {
                ...tempImageData.metadata,
                isTemporary: false,
                previousImageId: statistic?.imageId
              },
              uploadedBy: 'current-user' // TODO: Get actual user ID
            })
                      });
            
            // console.log('🔍 DEBUG: Image upload request body:', {
              // filename: `statistic-${Date.now()}.jpg`,
              // mimeType: 'image/jpeg',
              // size: tempImageData.data ? tempImageData.data.length * 0.75 : 1024,
              // hasData: !!tempImageData.data,
              // url: tempImageData.url,
              // source: tempImageData.metadata.source || 'upload',
              // metadataKeys: Object.keys(tempImageData.metadata || {})
            // }); // Removed for production

          if (imageResponse.ok) {
            const savedImage = await imageResponse.json();
            // console.log('🔍 DEBUG: Image saved successfully:', savedImage); // Removed for production
            // Update the statistic with the real image ID
            finalImageId = savedImage.id;
            tableData.imageId = savedImage.id;
            
            // Update ogImage with the correct URL based on the saved image
            if (tempImageData.metadata?.source === 'upload') {
              // Our uploaded image - use local URL
              const baseUrl = window.location.origin;
              tableData.ogImage = `${baseUrl}/api/images/serve/${savedImage.id}`;
            } else {
              // Stock image - use the original URL
              tableData.ogImage = tempImageData.url;
            }
            
            // Update ogImageAlt from metadata
            tableData.ogImageAlt = generateImageAttribution(tempImageData.metadata, tempImageData.url);
          } else {
            const errorText = await imageResponse.text();
            console.error('🔍 DEBUG: Failed to save image:', errorText);
            console.error('🔍 DEBUG: Response status:', imageResponse.status);
            console.error('🔍 DEBUG: Response headers:', Object.fromEntries(imageResponse.headers.entries()));
            throw new Error(`Failed to save image to database: ${errorText}`);
          }
        } catch (error) {
          console.error('Error saving image to database:', error);
          // Don't fail the save if image saving fails
        }
        }
      } else if (editedStatistic?.imageId && statistic && editedStatistic.imageId !== statistic.imageId) {
        try {
          // Mark existing image as permanent
          await fetch(`/api/images/${editedStatistic.imageId}/make-permanent`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              previousImageId: statistic.imageId
            })
          });
        } catch (error) {
          console.error('Error marking image as permanent:', error);
          // Don't fail the save if image marking fails
        }
      }

      // Remove temporary image ID from tableData if it exists
      if (tableData.imageId && tableData.imageId.startsWith('temp-')) {
        // console.log('🔍 DEBUG: Removing temporary image ID from tableData:', tableData.imageId); // Removed for production
        tableData.imageId = finalImageId;
      }

      // console.log('🔍 FRONTEND DEBUG: Sending data to backend:', JSON.stringify(tableData, null, 2)); // Removed for production

      const response = await fetch(`/api/statistics/${statisticId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(tableData)
      });

      if (!response.ok) {
        throw new Error('Failed to save statistic');
      }

      // Update the original statistic with the edited data and real image ID
      if (editedStatistic) {
        const updatedStatistic = {
          ...editedStatistic,
          imageId: finalImageId
        };
        setStatistic(updatedStatistic);
        setEditedStatistic(updatedStatistic);
      }
      setTempImageData(null); // Clear temporary image data
      setShowSavePreview(false);
      
      // Show success message (you can implement a toast notification here)
      // console.log('Statistic saved successfully!'); // Removed for production
    } catch (error) {
      console.error('Error saving statistic:', error);
      // Show error message (you can implement a toast notification here)
    }
  };

  const jsonExample = '{"male": 48, "female": 50}';

  const getChartTypeIcon = (type: ChartType) => {
    switch (type) {
      case 'bar': return <BarChart3 size={20} />;
      case 'pie': return <PieChart size={20} />;
      case 'radial': return <RotateCcw size={20} />;
      case 'line': return <TrendingUp size={20} />;
      case 'area': return <TrendingUp size={20} />;
      default: return <BarChart3 size={20} />;
    }
  };

  // Markdown rendering functions
  const renderMarkdown = (text: string): string => {
    if (!text) return '';

    return withMathMarkdown(text, (safeText) => {
      // Basic Markdown parsing
      let html = safeText
        // Headers (must be processed first, before line breaks)
        .replace(/^### (.*$)/gim, '<h3 class="article-h3">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="article-h2">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="article-h1">$1</h1>')
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
        // Images with attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Fuente: \[([^\]]+)\]\(([^)]+)\)\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Fuente: <a href="$4" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$3</a></em></div></div>')
        // Images with simple attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Fuente: ([^*]+)\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Fuente: $3</em></div></div>')
        // Images with AI attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Imagen generada con IA\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Imagen generada con IA</em></div></div>')
        // Regular images without attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /></div>')
        // Line breaks
        .replace(/\n/g, '<br>');

      // Process lists (ordered and unordered)
      html = processLists(html);

      // Process tables
      html = processTables(html);

      html = html.replace(/(<br\s*\/?>)+(\s*<h[1-3])/gi, '$2');
      html = html.replace(/(<\/h[1-3]>)(\s*<br\s*\/?>)+/gi, '$1');
      html = html.replace(/(<br\s*\/?>\s*){2,}/gi, '<span class="article-p-gap" aria-hidden="true"></span>');

      return html;
    });
  };

  const processLists = (html: string): string => {
    // Split by line breaks to process line by line
    const lines = html.split('<br>');
    const processedLines: string[] = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let listItems: string[] = [];
    let lastWasEmpty = false;

    const flushList = () => {
      if (listItems.length > 0) {
        if (inUnorderedList) {
          processedLines.push(`<ul class="list-disc list-outside space-y-1 ml-6 pl-2">${listItems.join('')}</ul>`);
        } else if (inOrderedList) {
          processedLines.push(`<ol class="list-decimal list-outside space-y-1 ml-6 pl-2">${listItems.join('')}</ol>`);
        }
        listItems = [];
        lastWasEmpty = false; // Reset empty line tracking after list
      }
      inUnorderedList = false;
      inOrderedList = false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check for unordered list item
      if (trimmedLine.startsWith('- ')) {
        if (!inUnorderedList) {
          flushList();
          inUnorderedList = true;
        }
        const content = trimmedLine.substring(2);
        listItems.push(`<li class="text-foreground">${content}</li>`);
        lastWasEmpty = false;
      }
      // Check for ordered list item
      else if (/^\d+\. /.test(trimmedLine)) {
        if (!inOrderedList) {
          flushList();
          inOrderedList = true;
        }
        const content = trimmedLine.replace(/^\d+\. /, '');
        listItems.push(`<li class="text-foreground">${content}</li>`);
        lastWasEmpty = false;
      }
      // Empty line
      else if (trimmedLine === '') {
        if (inUnorderedList || inOrderedList) {
          // Don't add empty lines inside lists
          continue;
        } else {
          // Only add empty line if previous line wasn't empty
          if (!lastWasEmpty) {
            processedLines.push('');
            lastWasEmpty = true;
          }
        }
      }
      // Regular line
      else {
        flushList();
        processedLines.push(line);
        lastWasEmpty = false;
      }
    }

    // Flush any remaining list
    flushList();

    return processedLines.join('<br>');
  };

  const processTables = (html: string): string => {
    // Split by line breaks to process line by line
    const lines = html.split('<br>');
    const processedLines: string[] = [];
    let tableLines: string[] = [];
    let inTable = false;

    const flushTable = () => {
      if (tableLines.length > 0) {
        const tableHtml = convertTableToHtml(tableLines);
        processedLines.push(tableHtml);
        tableLines = [];
      }
      inTable = false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check if line is a table row (starts with | and ends with |)
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        if (!inTable) {
          flushTable();
          inTable = true;
        }
        tableLines.push(trimmedLine);
      } else {
        flushTable();
        processedLines.push(line);
      }
    }

    // Flush any remaining table
    flushTable();

    return processedLines.join('<br>');
  };

  const convertTableToHtml = (tableLines: string[]): string => {
    if (tableLines.length < 2) return tableLines.join('<br>');

    const rows = tableLines.map(line => {
      // Remove leading and trailing |
      const cells = line.slice(1, -1).split('|').map(cell => cell.trim());
      return cells;
    });

    // First row is header, second row is separator (ignore it)
    const headerRow = rows[0];
    const dataRows = rows.slice(2); // Skip separator row

    let tableHtml = '<div class="overflow-x-auto my-4"><table class="min-w-full border border-surface-200 rounded-lg">';
    
    // Header
    tableHtml += '<thead class="bg-surface-50">';
    tableHtml += '<tr>';
    headerRow.forEach(cell => {
      tableHtml += `<th class="border border-surface-200 px-4 py-2 text-left font-semibold text-foreground">${cell}</th>`;
    });
    tableHtml += '</tr>';
    tableHtml += '</thead>';
    
    // Body
    tableHtml += '<tbody>';
    dataRows.forEach(row => {
      tableHtml += '<tr>';
      row.forEach(cell => {
        tableHtml += `<td class="border border-surface-200 px-4 py-2 text-foreground">${cell}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody>';
    
    tableHtml += '</table></div>';
    
    return tableHtml;
  };

  // Helper function to generate image attribution for ogImageAlt
  const generateImageAttribution = (metadata: any, imageUrl: string): string => {
    if (!metadata) return '';
    
    const { author, alt, caption, authorUrl } = metadata;
    
    // For stock images, create proper attribution
    if (author && alt) {
      if (author === 'Freepik') {
        return `${alt} - Foto por ${author}`;
      } else if (author === 'Pexels') {
        return `${alt} - Foto por ${author}`;
      } else {
        return `${alt} - Foto por ${author}`;
      }
    }
    
    // Fallback to alt text if no author
    if (alt) {
      return alt;
    }
    
    // Fallback to caption
    if (caption) {
      return caption;
    }
    
    // Final fallback
    return 'Imagen del artículo';
  };

  // Helper function to get full canonical URL
  const getFullCanonicalUrl = (relativeUrl: string): string => {
    if (!relativeUrl) return '';
    if (relativeUrl.startsWith('http')) return relativeUrl; // Already absolute
    return `${window.location.origin}${relativeUrl}`;
  };

  const getBlockColors = (blockType: string) => {
    switch (blockType) {
      case 'extracted':
        return {
          bg: 'bg-green-500',
          border: 'border-green-500',
          text: 'text-green-600',
          bgLight: 'bg-green-50',
          borderLight: 'border-green-200'
        };
      case 'estimation':
        return {
          bg: 'bg-yellow-500',
          border: 'border-yellow-500',
          text: 'text-yellow-600',
          bgLight: 'bg-yellow-50',
          borderLight: 'border-yellow-200'
        };
      case 'calculated':
        return {
          bg: 'bg-blue-500',
          border: 'border-blue-500',
          text: 'text-blue-600',
          bgLight: 'bg-blue-50',
          borderLight: 'border-blue-200'
        };
      case 'assumption':
        return {
          bg: 'bg-gray-500',
          border: 'border-gray-500',
          text: 'text-gray-600',
          bgLight: 'bg-gray-50',
          borderLight: 'border-gray-200'
        };
      default:
        return {
          bg: 'bg-gray-500',
          border: 'border-gray-500',
          text: 'text-gray-600',
          bgLight: 'bg-gray-50',
          borderLight: 'border-gray-200'
        };
    }
  };



  const renderMarkdownWithCharts = (text: string): React.ReactNode[] => {
    if (!text) return [];

    const parts: React.ReactNode[] = [];
    
    // Split by special elements first, preserving the delimiters
    const specialElementsRegex = /(\{\{chart:[^}]+\}\}|\{\{highlight:[^}]+\}\}|\{\{segment:[^}]+\}\}|\{\{fact:[^}]+\}\}|\{\{amazon:[^}]+\}\}|\{\{experience:[^}]+\}\})/g;
    const segments = text.split(specialElementsRegex);
    
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      // Check if this segment is a special element
      const chartMatch = segment.match(/\{\{chart:([^}]+)\}\}/);
      const highlightMatch = segment.match(/\{\{highlight:([^}]+)\}\}/);
      const segmentMatch = segment.match(/\{\{segment:([^}]+)\}\}/);
      const factMatch = segment.match(/\{\{fact:([^}]+)\}\}/);
      const amazonMatch = segment.match(/\{\{amazon:([^}]+)\}\}/);
      const experienceMatch = segment.match(/\{\{experience:([^}]+)\}\}/);
      
      if (chartMatch) {
        const chartId = chartMatch[1];
        const chart = editedStatistic?.data?.charts?.find(c => c.id === chartId);
        
        if (chart) {
          parts.push(
            <div key={`chart-${chartId}-${i}`} className="my-6 py-3">
              <div className="border border-surface-300 rounded-lg p-6 bg-surface-50">
                {renderCustomChart(chart)}
              </div>
            </div>
          );
        } else {
          // Fallback if chart not found
          parts.push(
            <div key={`chart-${chartId}-${i}`} className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4 text-center">
              <div className="text-blue-600 font-medium">📊 Gráfico: {chartId}</div>
              <div className="text-sm text-blue-500">Gráfico no encontrado</div>
            </div>
          );
        }
      } else if (highlightMatch) {
        const highlightId = highlightMatch[1];
        const highlightIndex = parseInt(highlightId.replace('H', '')) - 1;
        const highlight = editedStatistic?.highlights?.[highlightIndex];
        
        if (highlight) {
          parts.push(
            <div key={`highlight-${highlightId}-${i}`} className="my-6">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="text-base font-medium text-foreground leading-relaxed">
                  {highlight}
                </div>
              </div>
            </div>
          );
        } else {
          // Fallback if highlight not found
          parts.push(
            <div key={`highlight-${highlightId}-${i}`} className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4 text-center">
              <div className="text-orange-600 font-medium">💡 Highlight: {highlightId}</div>
              <div className="text-sm text-orange-500">Highlight no encontrado</div>
            </div>
          );
        }
      } else if (segmentMatch) {
        const segmentDimension = segmentMatch[1];
        const segment = editedStatistic?.data?.segments?.find(s => s.dimension === segmentDimension);
        
        if (segment) {
          parts.push(
            <div key={`segment-${segmentDimension}-${i}`} className="my-8">
              <div className="bg-white border border-surface-200 rounded-lg shadow-sm overflow-hidden">
                {/* Chart Header */}
                <div className="bg-surface-50 px-6 py-4 border-b border-surface-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {formatSegmentLabel(segment)}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Distribución por {segment.dimension_title || segment.dimension?.replace(/_/g, ' ') || 'segmento'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Chart Content */}
                <div className="p-6">
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed whitespace-pre-line">
                    {formatTextWithBreaks(segment.description)}
                  </p>
                  
                  {renderChart(segment.values, segment.dimension)}
                </div>
              </div>
            </div>
          );
        } else {
          // Fallback if segment not found
          parts.push(
            <div key={`segment-${segmentDimension}-${i}`} className="bg-green-50 border border-green-200 rounded-lg p-4 my-4 text-center">
              <div className="text-green-600 font-medium">📊 Segmentación: {segmentDimension}</div>
              <div className="text-sm text-green-500">Segmentación no encontrada</div>
            </div>
          );
        }
      } else if (factMatch) {
        const factId = factMatch[1];
        const fact = editedStatistic?.data?.blocks?.find(b => b.id === factId);
        
        if (fact) {
          const colors = getBlockColors(fact.type);
          const blockNumber = fact.id.replace('P', '');
          
          parts.push(
            <div key={`fact-${factId}-${i}`} className="my-4">
              {/* Fact Container - No badge, aligned to left */}
              <div className={`${colors.bgLight} border-l-4 ${colors.border} p-4 rounded-r-lg relative`}>
                {/* Title at the top */}
                <h4 className="text-xl font-semibold text-foreground mb-2 font-serif">
                  {fact.title}
                </h4>
                
                {/* Info Icon for Source */}
                {fact.source && (
                  <div className="absolute top-2 right-2">
                    <div className="relative">
                      <button 
                        className="w-6 h-6 bg-white/80 rounded-full flex items-center justify-center cursor-help hover:bg-white transition-colors"
                        onClick={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling;
                          const isVisible = !tooltip.classList.contains('opacity-0');
                          if (isVisible) {
                            tooltip.classList.add('opacity-0');
                            tooltip.classList.remove('opacity-100');
                          } else {
                            tooltip.classList.remove('opacity-0');
                            tooltip.classList.add('opacity-100');
                            // Close other tooltips
                            document.querySelectorAll('.tooltip-source').forEach(t => {
                              if (t !== tooltip) {
                                t.classList.add('opacity-0');
                                t.classList.remove('opacity-100');
                              }
                            });
                          }
                        }}
                      >
                        <svg className="w-4 h-4 text-muted-foreground" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                        </svg>
                      </button>
                      {/* Tooltip */}
                      <div className="tooltip-source absolute bottom-full right-0 mb-2 w-64 bg-black text-white text-sm rounded-lg p-3 opacity-0 transition-opacity z-20">
                        <div className="font-medium mb-1">Fuente:</div>
                        <div className="text-xs leading-relaxed">
                          {fact.source}
                          {fact.source_url && (
                            <div className="mt-2">
                              <a 
                                href={fact.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-300 hover:text-blue-200 underline"
                              >
                                Ver fuente original →
                              </a>
                            </div>
                          )}
                        </div>
                        {/* Arrow */}
                        <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Value with icon */}
                <div className="flex items-center gap-3 mb-2">
                  {fact.image_icon && (
                    <img 
                      src={fact.image_icon} 
                      alt={`Icono ${fact.title}`} 
                      className="w-10 h-10 object-contain"
                    />
                  )}
                  <div className={`text-4xl font-bold ${colors.text}`}>
                    {fact.value !== undefined && fact.value !== null 
                      ? (fact.value === 0 ? '0' : fact.value.toLocaleString())
                      : 'N/A'} <span dangerouslySetInnerHTML={{ __html: formatUnitDisplay(fact) }} />
                  </div>
                </div>
                
                {/* Description inside the container */}
                <p className="text-base text-foreground leading-snug mb-2 font-serif whitespace-pre-line">
                  {formatTextWithBreaks(fact.description)}
                </p>
                
                {/* Type label at the bottom */}
                <div className="text-sm text-muted-foreground uppercase tracking-wide">
                  {fact.type === 'extracted' && 'Dato extraído'}
                  {fact.type === 'estimation' && 'Estimación'}
                  {fact.type === 'calculated' && 'Cálculo'}
                  {fact.type === 'assumption' && 'Suposición'}
                </div>
              </div>
            </div>
          );
        } else {
          // Fallback if fact not found
          parts.push(
            <div key={`fact-${factId}-${i}`} className="bg-red-50 border border-red-200 rounded-lg p-4 my-4 text-center">
              <div className="text-red-600 font-medium">❌ Fact: {factId}</div>
              <div className="text-sm text-red-500">Fact no encontrado</div>
            </div>
          );
        }
      } else if (amazonMatch) {
        const productId = amazonMatch[1];
        const product = amazonProducts.find(p => p.id === productId);
        
        if (product) {
          parts.push(
            <div key={`amazon-${productId}-${i}`} className="my-6">
              <AmazonProductCard
                product={product}
                size="medium"
                showFeatures={true}
              />
            </div>
          );
        } else {
          // Fallback if product not found
          parts.push(
            <div key={`amazon-${productId}-${i}`} className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4 text-center">
              <div className="text-orange-600 font-medium">🛒 Producto Amazon: {productId}</div>
              <div className="text-sm text-orange-500">Producto no encontrado</div>
            </div>
          );
        }
      } else if (experienceMatch) {
        // Interactive experience embed — marco editorial sin chrome extra
        const experienceId = experienceMatch[1];
        parts.push(
          <div key={`experience-${experienceId}-${i}`} className="my-6">
            <div
              className="overflow-hidden bg-white"
              style={{ border: '1px solid #1a1a1a' }}
            >
              <iframe
                src={`/api/experiences/${experienceId}/embed`}
                title="Experiencia interactiva"
                width="100%"
                height="500"
                className="border-0 block w-full bg-white"
                loading="lazy"
              />
            </div>
          </div>
        );
      } else {
        // Regular text segment - render with markdown and preserve line breaks
        const html = renderMarkdown(segment);
        if (html.trim()) {
          const headingOnly = /^\s*<h[1-3]\b[^>]*>[\s\S]*?<\/h[1-3]>\s*$/i.test(html);
          parts.push(
            <div 
              key={`text-${i}`}
              dangerouslySetInnerHTML={{ __html: html }}
              className={headingOnly ? 'article-paragraph article-heading-block' : 'article-paragraph'}
            />
          );
        }
      }
    }
    
    return parts;
  };

  const renderCustomChart = (chart: StatisticChart) => {
    // Only log when actually rendering (not during editing)
    if (!modalConfig || modalConfig.type !== 'chart') {
      // console.log('🔍 RENDERING CUSTOM CHART:', {
        // chartId: chart.id,
        // values: chart.values,
        // orientation: chart.orientation,
        // xAxisTitle: chart.xAxisTitle,
        // yAxisTitle: chart.yAxisTitle
      // }); // Removed for production
    }
    
    if (!chart.values || typeof chart.values !== 'object') {
      // console.warn('Invalid data for custom chart:', chart.values); // Removed for production
      return <div className="text-muted-foreground">Datos no válidos para el gráfico</div>;
    }
    
    // Use preserved order if available, otherwise fall back to Object.entries
    const orderedKeys = chart.valuesOrder || Object.keys(chart.values);
    const entries = orderedKeys.map(key => [key, chart.values[key]]);
    
    // console.log('🔍 CHART RENDERING ORDER:', {
      // chartId: chart.id,
      // valuesOrder: chart.valuesOrder,
      // orderedKeys,
      // originalKeys: Object.keys(chart.values)
    // }); // Removed for production
    // Values are already numbers
    const numericValues = entries.map(([, value]) => typeof value === 'number' ? value : 0);
    const total = numericValues.reduce((sum, value) => sum + value, 0);
    const maxValue = Math.max(...numericValues, 1); // Ensure maxValue is at least 1
    // Add 20% margin to the scale so the highest bar doesn't reach the top
    const scaleMaxValue = maxValue * 1.2;
    
    if (chart.orientation === 'horizontal') {
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            {entries.map(([key, value], index) => {
              const numericValue = numericValues[index];
              const percentage = (numericValue / scaleMaxValue) * 100;
              const iconUrl = chart.icons?.[key];
              
              return (
                <div key={key} className="space-y-2 group relative">
                  <div className="flex justify-between text-sm items-center">
                    <div className="flex items-center gap-2">
                      {iconUrl && (
                        <img 
                          src={iconUrl} 
                          alt={`Icono ${key}`} 
                          className="w-6 h-6 object-contain"
                        />
                      )}
                      <span className="font-medium">{key}</span>
                    </div>
                    <span className="text-muted-foreground">{value}{chart.unit ? ` (${chart.unit})` : ''}</span>
                  </div>
                   <div className="w-full bg-surface-200 rounded-r-full h-6 relative cursor-pointer">
                     <div 
                       className="bg-gradient-to-r from-blue-500 to-purple-600 h-6 rounded-r-full transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
                       style={{ width: `${percentage}%` }}
                       title={`${key}: ${numericValue}${chart.unit ? ` ${chart.unit}` : ''}`}
                     />
                     
                     {/* Tooltip */}
                     <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
                       <div className="font-semibold">{key}</div>
                       <div className="text-xs opacity-90">{numericValue}{chart.unit ? ` ${chart.unit}` : ''}</div>
                       <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                     </div>
                   </div>
                </div>
              );
            })}
          </div>
          
          {/* Chart description as caption */}
          {chart.description && (
            <div className="text-sm text-muted-foreground text-center italic border-t border-surface-200 pt-3">
              {chart.description}
            </div>
          )}
        </div>
      );
    } else if (chart.orientation === 'radial') {
      // Radial chart
      return (
        <div className="space-y-4">
          <div className="flex flex-col items-center">
            {/* Radial Chart Container */}
            <div className="relative w-80 h-80 mb-6">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
                {/* Background circle */}
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="8"
                />
                
                {/* Data segments */}
                {entries.map(([key, value], index) => {
                  const numericValue = numericValues[index];
                  const percentage = (numericValue / total) * 100;
                  const angle = (percentage / 100) * 360;
                  const radius = 80;
                  const circumference = 2 * Math.PI * radius;
                  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
                  
                  return (
                    <g key={key}>
                      <circle
                        cx="100"
                        cy="100"
                        r={radius}
                        fill="none"
                        stroke={colors[index % colors.length]}
                        strokeWidth="8"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={index === 0 ? 0 : -circumference * (entries.slice(0, index).reduce((sum, [, v]) => sum + (v / total), 0))}
                        className="transition-all duration-500"
                        strokeLinecap="round"
                      />
                    </g>
                  );
                })}
              </svg>
              
              {/* Center content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{total}</div>
                  <div className="text-sm text-muted-foreground">Total</div>
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-md">
              {entries.map(([key, value], index) => {
                const numericValue = numericValues[index];
                const percentage = (numericValue / total) * 100;
                const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
                const iconUrl = chart.icons?.[key];
                
                return (
                  <div key={key} className="flex items-center gap-3 p-2 rounded-lg bg-surface-50">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      {iconUrl && (
                        <img 
                          src={iconUrl} 
                          alt={`Icono ${key}`} 
                          className="w-5 h-5 object-contain"
                        />
                      )}
                      <span className="font-medium text-sm">{key}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{numericValue}{chart.unit ? ` (${chart.unit})` : ''}</div>
                      <div className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Chart description as caption */}
          {chart.description && (
            <div className="text-sm text-muted-foreground text-center italic border-t border-surface-200 pt-6 mt-12">
              {chart.description}
            </div>
          )}
        </div>
      );
    } else {
      // Vertical bars
      const yAxisLabel = chart.yAxisTitle || 'Eje Y';
      const xAxisLabel = chart.xAxisTitle || 'Eje X';

      return (
        <div className="space-y-4">
          <div className="flex items-stretch gap-2">
            {/* Y-axis label — dedicated column so long titles never overlap bars */}
            <div className="flex shrink-0 items-center justify-center w-6 sm:w-8 self-stretch">
              <span
                className="text-xs text-muted-foreground font-medium leading-tight max-h-80 rotate-180"
                style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
              >
                {yAxisLabel}
              </span>
            </div>

            {/* Chart plot area */}
            <div className="relative flex-1 min-w-0">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-surface-300" />
              <div className="absolute left-0 right-0 bottom-0 h-px bg-surface-300" />
              <div className="absolute right-0 bottom-0 translate-y-2 text-xs text-muted-foreground font-medium">
                {xAxisLabel}
              </div>

              <div className="flex items-end justify-between gap-2 h-80 pl-2 pb-6 pt-8">
            {entries.map(([key, value], index) => {
              const numericValue = numericValues[index];
              const percentage = (numericValue / scaleMaxValue) * 100;
              const minHeight = 4; // Altura mínima en píxeles
              // Reduce available height to leave space for value above (pt-8 = 32px, so use 288px instead of 320px)
              const height = Math.max(minHeight, (percentage / 100) * 288);
              const iconUrl = chart.icons?.[key];
              
              return (
                <div key={key} className="flex flex-col items-center flex-1 relative group">
              {/* Value displayed above the bar */}
              <div className="text-sm font-semibold text-foreground mb-1 text-center absolute top-0 left-0 right-0">
                {numericValue}{chart.unit ? ` (${chart.unit})` : ''}
              </div>
                  
                  {/* The bar - positioned from bottom */}
                  <div 
                    className="w-full bg-gradient-to-t from-blue-500 to-purple-600 rounded-t transition-all duration-300 relative cursor-pointer hover:from-blue-600 hover:to-purple-700"
                    style={{ height: `${height}px` }}
                    title={`${key}: ${numericValue}${chart.unit ? ` ${chart.unit}` : ''}`}
                  />
                  
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
                    <div className="font-semibold">{key}</div>
                    <div className="text-xs opacity-90">{numericValue}{chart.unit ? ` ${chart.unit}` : ''}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                  
                  {/* Fixed space for labels - ensures all bars end at same point */}
                  <div className="h-16 w-full"></div>
                  
                  {/* Label below the bar - positioned absolutely */}
                  <div className="absolute bottom-0 left-0 right-0 text-xs text-muted-foreground text-center">
                    <div className="flex items-center gap-1 justify-center mb-1">
                      {iconUrl && (
                        <img 
                          src={iconUrl} 
                          alt={`Icono ${key}`} 
                          className="w-5 h-5 object-contain"
                        />
                      )}
                    </div>
                    <div className="font-medium leading-tight px-1">
                      {key}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
            </div>
          </div>
          
          {/* Chart description as caption */}
          {chart.description && (
            <div className="text-sm text-muted-foreground text-center italic border-t border-surface-200 pt-3">
              {chart.description}
            </div>
          )}
        </div>
      );
    }
  };

  const renderChart = (data: Record<string, number>, title: string) => {
    if (!data || typeof data !== 'object') {
      // console.warn('Invalid data for chart:', data); // Removed for production
      return <div className="text-muted-foreground">Datos no válidos para el gráfico</div>;
    }
    
    const entries = Object.entries(data);
    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    
    switch (chartType) {
      case 'bar':
        return (
          <div className="space-y-3">
            {entries.map(([key, value]) => {
              const percentage = (value / total) * 100;
              return (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{key}</span>
                    <span className="text-muted-foreground">{value} ({percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-surface-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      
      case 'pie':
        return (
          <div className="relative w-64 h-64 mx-auto">
            <svg className="w-full h-full transform -rotate-90">
              {entries.map(([key, value], index) => {
                const percentage = (value / total) * 100;
                const angle = (percentage / 100) * 360;
                const previousAngles = entries
                  .slice(0, index)
                  .reduce((sum, [, val]) => sum + (val / total) * 360, 0);
                
                const x1 = 50 + 40 * Math.cos((previousAngles * Math.PI) / 180);
                const y1 = 50 + 40 * Math.sin((previousAngles * Math.PI) / 180);
                const x2 = 50 + 40 * Math.cos(((previousAngles + angle) * Math.PI) / 180);
                const y2 = 50 + 40 * Math.sin(((previousAngles + angle) * Math.PI) / 180);
                
                const largeArcFlag = angle > 180 ? 1 : 0;
                
                return (
                  <path
                    key={key}
                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                    fill={`hsl(${index * 60}, 70%, 60%)`}
                    className="transition-all duration-300 hover:opacity-80"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold">{total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
            </div>
          </div>
        );
      
      case 'radial':
        return (
          <div className="space-y-4">
            {entries.map(([key, value], index) => {
              const percentage = (value / total) * 100;
              const angle = (percentage / 100) * 360;
              const color = `hsl(${index * 60}, 70%, 60%)`;
              
              return (
                <div key={key} className="flex items-center gap-4">
                  <div className="relative w-16 h-16">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="var(--surface-300)"
                        strokeWidth="4"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke={color}
                        strokeWidth="4"
                        strokeDasharray={`${(angle / 360) * 175.93} 175.93`}
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-bold">{percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{key}</div>
                    <div className="text-sm text-muted-foreground">{value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      
      default:
        return <div>Chart type not implemented</div>;
    }
  };

  const renderEvolutionChart = () => {
    if (!statistic || !selectedYear || !statistic.data.evolution || !statistic.data.evolution.values) return null;
    
    const yearData = statistic.data.evolution.values.find(v => v.year === selectedYear);
    if (!yearData) return null;
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Evolución Histórica</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (statistic.data.evolution && statistic.data.evolution.years) {
                  const currentIndex = statistic.data.evolution.years.indexOf(selectedYear);
                  if (currentIndex > 0) {
                    setSelectedYear(statistic.data.evolution.years[currentIndex - 1]);
                  }
                }
              }}
              disabled={!statistic.data.evolution || !statistic.data.evolution.years || statistic.data.evolution.years.indexOf(selectedYear) === 0}
              className="p-2 rounded-lg hover:bg-surface-200 disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-lg font-bold">{selectedYear}</span>
            <button
              onClick={() => {
                if (statistic.data.evolution && statistic.data.evolution.years) {
                  const currentIndex = statistic.data.evolution.years.indexOf(selectedYear);
                  if (currentIndex < statistic.data.evolution.years.length - 1) {
                    setSelectedYear(statistic.data.evolution.years[currentIndex + 1]);
                  }
                }
              }}
              disabled={!statistic.data.evolution || !statistic.data.evolution.years || statistic.data.evolution.years.indexOf(selectedYear) === statistic.data.evolution.years.length - 1}
              className="p-2 rounded-lg hover:bg-surface-200 disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        
        <div className="statemia-card">
          <div className="text-center mb-4">
            <div className="text-3xl font-bold text-primary">{yearData.value.toLocaleString()}</div>
            <div className="text-muted-foreground">{yearData.description}</div>
          </div>
          
          {Object.entries(yearData.segments).map(([segmentName, segmentData]) => (
            <div key={segmentName} className="mb-6">
              <h4 className="font-semibold mb-3 capitalize">{segmentName.replace('_', ' ')}</h4>
              {renderChart(segmentData, segmentName)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <div className="statemia-spinner mx-auto mb-4" />
            <p>Cargando estadística...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !statistic) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Error al cargar la estadística
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/statistics')}
              className="btn-primary"
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Function to translate category names from English to Spanish
  const translateCategory = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'politics': t('statistics.politics'),
      'society': t('statistics.society'),
      'economy': t('statistics.economy'),
      'culture': t('statistics.culture'),
      'technology': t('statistics.technology'),
      'entertainment': t('statistics.entertainment'),
      'sports': t('statistics.sports'),
      'science': t('statistics.science'),
      'lifestyle': t('statistics.lifestyle'),
      'unclassified': t('statistics.unclassified')
    };
    
    return categoryMap[category] || category;
  };

  return (
    <div className="statemia-container">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="statemia-container">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/statistics')}
              className="btn-secondary flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Volver
            </button>
            
            <div className="flex items-center gap-2">
              <button className="btn-secondary flex items-center gap-2">
                <BarChart3 size={16} />
                Exportar
              </button>
              {editedStatistic && (
                <>
                  <FavoriteButton
                    statisticId={editedStatistic.id}
                    initialFavorited={isFavorited}
                    initialCount={metrics?.favoritesCount || 0}
                    onToggle={(isFavorited, count) => {
                      setIsFavorited(isFavorited);
                      setMetrics(prev => prev ? { ...prev, favoritesCount: count } : null);
                    }}
                    size="sm"
                    showCount={true}
                  />
                  <ShareButtons
                    url={`${window.location.origin}/statistics/${editedStatistic.slug}`}
                    title={editedStatistic.title}
                    description={editedStatistic.lead || editedStatistic.metaDescription || ''}
                    hashtags={editedStatistic.tags || []}
                    variant="compact"
                  />
                </>
              )}
              <button 
                onClick={handleSave}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={16} />
                Guardar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-20 pt-16"></div>

      {/* Hero Section */}
      <div className="statemia-card mb-6 statemia-article article-editor">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Image */}
          <div className="lg:col-span-1">
            <ImageManager
              currentImageId={editedStatistic?.imageId || statistic.imageId}
              currentImageUrl={tempImageData?.url}
              onImageChange={(imageData) => handleSaveField('imageId', imageData)}
              title={editedStatistic?.title || statistic.title}
              tags={editedStatistic?.tags || statistic.tags}
            />
          </div>

          {/* Content */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              {/* Category Header - EL PAÍS Style */}
              {(editedStatistic?.category || statistic.category) && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {translateCategory((editedStatistic?.category || statistic.category) || '')}
                  </h2>
                  <div className="w-full h-0.5 bg-foreground mb-4"></div>
                </div>
              )}
              
              <div className="flex items-center gap-2 mb-2">
                <h1 className="article-title text-foreground">
                  {editedStatistic?.title || statistic.title}
                </h1>
                <button
                  onClick={() => handleOpenModal('text', 'Editar Título', {
                    field: 'title',
                    value: editedStatistic?.title || statistic.title
                  })}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                >
                  <Edit size={16} />
                </button>
              </div>
              
              {/* Lead Field */}
              <div className="flex items-start gap-2 mb-3">
                <p className="article-lead text-foreground leading-relaxed flex-1">
                  {editedStatistic?.lead || statistic.lead || 'Sin lead definido'}
                </p>
                <button
                  onClick={() => handleOpenModal('text', 'Editar Lead', {
                    field: 'lead',
                    value: editedStatistic?.lead || statistic.lead || ''
                  })}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors flex-shrink-0"
                >
                  <Edit size={16} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Descripción</h3>
                  <button
                    onClick={() => handleOpenModal('markdown', 'Editar Descripción', {
                      field: 'description',
                      value: editedStatistic?.description || statistic.description,
                      charts: editedStatistic?.data?.charts || [],
                      highlights: editedStatistic?.highlights?.map((text, index) => ({ id: `H${index + 1}`, text })) || [],
                      segments: editedStatistic?.data?.segments || [],
                      fullscreen: true
                    })}
                    className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                </div>
                
                {/* Render description with Markdown and charts */}
                <div className="article-body font-serif">
                  {renderMarkdownWithCharts(editedStatistic?.description || statistic.description)}
                </div>
              </div>



              {/* Conclusion Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Conclusión</h3>
                  <button
                    onClick={() => handleOpenModal('markdown', 'Editar Conclusión', {
                      field: 'conclusion',
                      value: editedStatistic?.conclusion || '',
                      charts: editedStatistic?.data?.charts || [],
                      highlights: editedStatistic?.highlights?.map((text, index) => ({ id: `H${index + 1}`, text })) || [],
                      segments: editedStatistic?.data?.segments || [],
                      fullscreen: true
                    })}
                    className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                </div>
                
                {/* Display conclusion with Markdown and charts rendering */}
                <div className="article-body font-serif">
                  {editedStatistic?.conclusion ? (
                    renderMarkdownWithCharts(editedStatistic.conclusion)
                  ) : (
                    <p className="italic">No hay conclusión definida</p>
                  )}
                </div>
              </div>

              {/* Radar Section - Premium Content */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Radar</h3>
                  <button
                    onClick={() => handleOpenModal('markdown', 'Editar Radar', {
                      field: 'radar',
                      value: editedStatistic?.radar || '',
                      charts: editedStatistic?.data?.charts || [],
                      highlights: editedStatistic?.highlights?.map((text, index) => ({ id: `H${index + 1}`, text })) || [],
                      segments: editedStatistic?.data?.segments || [],
                      fullscreen: true
                    })}
                    className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                </div>
                
                {/* Display radar with Markdown and charts rendering */}
                <div className="article-body font-serif">
                  {editedStatistic?.radar ? (
                    renderMarkdownWithCharts(editedStatistic.radar)
                  ) : (
                    <p className="italic">No hay radar definido</p>
                  )}
                </div>
                
                {/* Premium Notice for Editors */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-800 mb-1">Contenido Premium</h4>
                      <p className="text-sm text-blue-700">
                        Este contenido solo será visible para usuarios con suscripción premium y administradores.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-muted-foreground" />
                {editingField === 'location' ? (
                  <input
                    type="text"
                    value={editedStatistic?.location || ''}
                    onChange={(e) => handleSaveField('location', e.target.value)}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
                    className="text-sm bg-transparent border-b border-primary focus:outline-none"
                    autoFocus
                  />
                ) : (
                  <span className="text-sm">{editedStatistic?.location || statistic.location}</span>
                )}
                <button
                  onClick={() => handleEditField('location')}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                >
                  <Edit size={12} />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-muted-foreground" />
                <span className="text-sm">{new Date(statistic.createdAt).toLocaleDateString()}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <User size={16} className="text-muted-foreground" />
                {editingField === 'author' ? (
                  <select
                    value={editedStatistic?.authorId || ''}
                    onChange={(e) => {
                      const selected = authors.find((a) => a.id === e.target.value);
                      if (!editedStatistic) return;
                      setEditedStatistic({
                        ...editedStatistic,
                        authorId: selected?.id || null,
                        author: selected?.name || editedStatistic.author,
                      });
                      setEditingField(null);
                    }}
                    onBlur={() => setEditingField(null)}
                    className="text-sm bg-transparent border-b border-primary focus:outline-none max-w-[200px]"
                    autoFocus
                  >
                    {authors.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.isDefault ? ' (por defecto)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm">{editedStatistic?.author || statistic.author}</span>
                )}
                <button
                  onClick={() => handleEditField('author')}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                >
                  <Edit size={12} />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-muted-foreground" />
                {editingField === 'language' ? (
                  <select
                    value={editedStatistic?.language || ''}
                    onChange={(e) => handleSaveField('language', e.target.value)}
                    onBlur={() => setEditingField(null)}
                    className="text-sm bg-transparent border-b border-primary focus:outline-none"
                    autoFocus
                  >
                    <option value="es">ES</option>
                    <option value="en">EN</option>
                    <option value="fr">FR</option>
                    <option value="de">DE</option>
                  </select>
                ) : (
                  <span className="text-sm">{(editedStatistic?.language || statistic.language).toUpperCase()}</span>
                )}
                <button
                  onClick={() => handleEditField('language')}
                  className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                >
                  <Edit size={12} />
                </button>
              </div>
            </div>

            {/* Category */}
            <div className="flex items-center gap-2">
              <Tag size={16} className="text-muted-foreground" />
              {editingField === 'category' ? (
                <select
                  value={editedStatistic?.category || ''}
                  onChange={(e) => handleSaveField('category', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  className="text-sm bg-transparent border-b border-primary focus:outline-none"
                  autoFocus
                >
                  <option value="">Sin categoría</option>
                  <option value="politics">Política</option>
                  <option value="society">Sociedad</option>
                  <option value="economy">Economía</option>
                  <option value="culture">Cultura</option>
                  <option value="entertainment">Entretenimiento</option>
                  <option value="sports">Deportes</option>
                  <option value="technology">Tecnología</option>
                  <option value="science">Ciencia</option>
                  <option value="lifestyle">Lifestyle</option>
                  <option value="unclassified">Inclasificables</option>
                </select>
              ) : (
                <span className="text-sm">
                  {editedStatistic?.category ? 
                    translateCategory(editedStatistic.category) : 
                    'Sin categoría'
                  }
                </span>
              )}
              <button
                onClick={() => handleEditField('category')}
                className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
              >
                <Edit size={12} />
              </button>
            </div>

            {/* Featured Article Section */}
            <div className="border-t border-surface-300 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Star size={16} />
                  <h3 className="text-lg font-semibold">Artículo Destacado</h3>
                </div>
              </div>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={editedStatistic?.featured || false}
                  onChange={(e) => setEditedStatistic(prev => prev ? {
                    ...prev,
                    featured: e.target.checked
                  } : null)}
                  className="w-4 h-4 text-primary bg-background border-gray-300 rounded focus:ring-primary focus:ring-2"
                />
                <label htmlFor="featured" className="text-sm font-medium text-foreground">
                  Marcar como artículo destacado para portada
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Solo un artículo puede estar destacado a la vez. Al marcar este, se desmarcará automáticamente el anterior.
              </p>
            </div>

            {/* SEO Section */}
            <div className="border-t border-surface-300 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSeoExpanded(!seoExpanded)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
                  >
                    {seoExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <Search size={16} />
                    <h3 className="text-lg font-semibold">SEO y Meta Tags</h3>
                  </button>
                </div>
                <button
                  onClick={async () => {
                    // Generate canonical URL based on slug (relative path)
                    const canonicalUrl = editedStatistic?.slug 
                      ? `/statistics/${editedStatistic.slug}`
                      : editedStatistic?.canonicalUrl || '';
                    
                    // Prepare OG Image data - if ogImage is empty but we have an imageId, generate the URL
                    let ogImageUrl = editedStatistic?.ogImage || '';
                    let ogImageAlt = editedStatistic?.ogImageAlt || '';
                    
                    if (!ogImageUrl && editedStatistic?.imageId) {
                      // Try to get the image URL from the current imageId
                      try {
                        const imageResponse = await fetch(`/api/images/${editedStatistic.imageId}/metadata`);
                        if (imageResponse.ok) {
                          const imageData = await imageResponse.json();
                          if (imageData.metadata?.source === 'upload') {
                            // Our image - use local URL
                            const baseUrl = window.location.origin;
                            ogImageUrl = `${baseUrl}/api/images/serve/${editedStatistic.imageId}`;
                          } else {
                            // Stock image - use external URL
                            ogImageUrl = imageData.url;
                          }
                          ogImageAlt = generateImageAttribution(imageData.metadata, imageData.url);
                        }
                      } catch (error) {
                        console.error('Error fetching image metadata for SEO modal:', error);
                      }
                    }
                    
                    handleOpenModal('seo', 'Editar SEO', {
                      metaTitle: editedStatistic?.metaTitle || '',
                      metaDescription: editedStatistic?.metaDescription || '',
                      keywords: editedStatistic?.keywords?.join(', ') || '',
                      ogImage: ogImageUrl,
                      ogImageAlt: ogImageAlt,
                      canonicalUrl: canonicalUrl
                    });
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit size={16} />
                  Editar SEO
                </button>
              </div>
              
              {seoExpanded && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">Meta Title:</div>
                    <div className="bg-surface-100 p-2 rounded border">
                      {editedStatistic?.metaTitle || 'No especificado'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Meta Description:</div>
                    <div className="bg-surface-100 p-2 rounded border">
                      {editedStatistic?.metaDescription || 'No especificada'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Keywords:</div>
                    <div className="bg-surface-100 p-2 rounded border">
                      {editedStatistic?.keywords?.join(', ') || 'No especificadas'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">OG Image:</div>
                    <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                      {editedStatistic?.ogImage || 'No especificada'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">OG Image Alt:</div>
                    <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                      {editedStatistic?.ogImageAlt || 'No especificada'}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-muted-foreground mb-1">Canonical URL:</div>
                    <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                      {editedStatistic?.canonicalUrl ? getFullCanonicalUrl(editedStatistic.canonicalUrl) : 'No especificada'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex items-start gap-2">
              <div className="flex flex-wrap gap-2 flex-1">
                {editedStatistic?.tags && editedStatistic.tags.length > 0 ? (
                  editedStatistic.tags.map((tag, index) => (
                    <span
                      key={`tag-${tag}-${index}`}
                      className="px-3 py-1 bg-surface-200 text-surface-600 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">No hay tags</span>
                )}
              </div>
              <button
                onClick={() => handleOpenModal('tags', 'Editar Tags', {
                  value: editedStatistic?.tags?.join(', ') || ''
                })}
                className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors flex-shrink-0"
              >
                <Edit size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>


            {/* Calculation Blocks */}
      <div className="statemia-card mb-6">
        <h2 className="text-xl font-semibold mb-4">Proceso de Cálculo</h2>
        <div className="space-y-4">
          {editedStatistic?.data.blocks.map((block, index) => (
            <div key={block.id}>
              {/* Block Content */}
              <div className={`border border-surface-300 rounded-lg p-4 ${block.hidden ? 'opacity-60 bg-surface-50' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-light text-muted-foreground bg-surface-100 px-2 py-1 rounded border">
                      {block.id}
                    </span>
                    <h3 className="font-semibold">{block.title}</h3>
                    {block.hidden && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                        Oculto
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      block.type === 'calculated' ? 'bg-blue-100 text-blue-800' :
                      block.type === 'estimation' ? 'bg-yellow-100 text-yellow-800' :
                      block.type === 'extracted' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {block.type}
                    </span>
                    <button
                      onClick={() => handleOpenModal('block', `Editar Bloque: ${block.title}`, {
                        blockId: block.id,
                        block: cleanBlockData(block)
                      })}
                      className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 mb-2">
                  {block.image_icon && (
                    <img 
                      src={block.image_icon} 
                      alt={`Icono ${block.title}`} 
                      className="w-8 h-8 object-contain"
                    />
                  )}
                  <div className="text-2xl font-bold text-primary">
                    {block.value !== undefined && block.value !== null 
                      ? (block.value === 0 ? '0' : block.value.toLocaleString())
                      : 'N/A'} <span dangerouslySetInnerHTML={{ __html: formatUnitDisplay(block) }} />
                  </div>
                </div>
                
                {/* Show dependencies for extracted, estimation and assumption blocks */}
                {(block.type === 'extracted' || block.type === 'estimation' || block.type === 'assumption') && editedStatistic && (
                (() => {
                  const dependentBlocks = editedStatistic.data.blocks.filter(b => 
                    b.type === 'calculated' && 
                    b.formula && 
                    detectDependencies(b.formula).includes(block.id)
                  );
                  
                  if (dependentBlocks.length > 0) {
                    return (
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">Actualiza automáticamente:</span> {dependentBlocks.map(b => b.id).join(', ')}
                      </div>
                    );
                  }
                  return null;
                })()
              )}
              
              <p className="text-sm text-muted-foreground mb-2 whitespace-pre-line">
                {formatTextWithBreaks(block.description)}
              </p>
              
              {block.formula && (
                <div className="text-sm font-mono bg-surface-200 p-2 rounded mb-2">
                  {block.formula}
                </div>
              )}
              
              {block.source && (
                <div className="text-xs text-muted-foreground">
                  Fuente: {block.source}
                </div>
              )}
              </div>
              
              {/* Add Block Button - Between blocks */}
              {index < (editedStatistic?.data.blocks.length || 0) - 1 && (
                <div className="relative my-4 group">
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => handleAddBlock(index + 1)}
                      className="bg-primary text-white rounded-full p-2 shadow-lg hover:bg-primary/90 transition-colors z-10"
                      title="Añadir bloque aquí"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="h-1 bg-surface-200 group-hover:bg-primary/20 transition-colors rounded-full"></div>
                </div>
              )}
            </div>
          ))}
          
          {/* Add Block Button at the end */}
          <div className="relative group">
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <button
                onClick={() => handleAddBlock(editedStatistic?.data.blocks.length || 0)}
                className="bg-primary text-white rounded-full p-2 shadow-lg hover:bg-primary/90 transition-colors pointer-events-auto z-10"
                title="Añadir bloque al final"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="border-2 border-dashed border-surface-300 rounded-lg p-8 group-hover:border-primary/50 transition-colors">
              <div className="text-center text-muted-foreground">
                <Plus size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Añadir nuevo bloque</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Highlights Management */}
      <div className="statemia-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Gestión de Highlights</h2>
          <button
            onClick={() => handleOpenModal('highlights', 'Editar Highlights', {
              field: 'highlights',
              value: editedStatistic?.highlights || []
            })}
            className="btn-primary flex items-center gap-2"
          >
            <Edit size={16} />
            Editar Highlights
          </button>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <span className="text-4xl mb-4 block">💡</span>
          <p className="text-lg font-medium mb-2">Gestión de Highlights</p>
          <p className="text-sm">
            {editedStatistic?.highlights && editedStatistic.highlights.length > 0 ? (
              <>
                {editedStatistic.highlights.length} highlight{editedStatistic.highlights.length !== 1 ? 's' : ''} disponible{editedStatistic.highlights.length !== 1 ? 's' : ''}. 
                Usa el botón 💡 en los editores de descripción y conclusión para insertarlos en el contenido.
              </>
            ) : (
              <>
                Crea highlights aquí y luego úsalos en la descripción o conclusión con el botón 💡 para insertarlos en el contenido del artículo.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Related Links Management */}
      <div className="statemia-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Enlaces Relacionados</h2>
          <button
            onClick={() => handleOpenModal('related-links', 'Editar Enlaces Relacionados', {
              field: 'relatedLinks',
              value: editedStatistic?.relatedLinks || []
            })}
            className="btn-primary flex items-center gap-2"
          >
            <Edit size={16} />
            Editar Enlaces
          </button>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <span className="text-4xl mb-4 block">🔗</span>
          <p className="text-lg font-medium mb-2">Enlaces Relacionados</p>
          <p className="text-sm">
            {editedStatistic?.relatedLinks && editedStatistic.relatedLinks.length > 0 ? (
              <>
                {editedStatistic.relatedLinks.length} enlace{editedStatistic.relatedLinks.length !== 1 ? 's' : ''} relacionado{editedStatistic.relatedLinks.length !== 1 ? 's' : ''} configurado{editedStatistic.relatedLinks.length !== 1 ? 's' : ''}.
                Aparecerán al final del artículo como "También te puede interesar".
              </>
            ) : (
              <>
                Añade enlaces a otros artículos relacionados. Aparecerán al final del artículo como "También te puede interesar".
              </>
            )}
          </p>
        </div>
      </div>

      {/* Amazon Products Management */}
      <div className="statemia-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Productos Amazon</h2>
        </div>
        <AmazonProductsManager 
          onProductsChange={fetchAmazonProducts}
          relatedAdProducts={editedStatistic?.relatedAdProducts || []}
          statisticId={editedStatistic?.id}
          onRelatedProductsChange={(productIds) => {
            // console.log('🔍 AMAZON EDIT DEBUG: Related products changed:', productIds); // Removed for production
            // console.log('🔍 AMAZON EDIT DEBUG: Current editedStatistic.id:', editedStatistic?.id); // Removed for production
            if (editedStatistic) {
              setEditedStatistic({
                ...editedStatistic,
                relatedAdProducts: productIds
              });
              // console.log('🔍 AMAZON EDIT DEBUG: Updated editedStatistic.relatedAdProducts:', productIds); // Removed for production
            }
          }}
        />
      </div>

      {/* Create Chart Button */}
      <div className="statemia-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Gráficos Personalizados</h2>
          <button
            onClick={handleAddChart}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Create Chart
          </button>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Crear Gráficos Personalizados</p>
          <p className="text-sm">
            Añade gráficos personalizados basados en los datos de tus bloques para visualizar mejor tus estadísticas.
          </p>
        </div>
      </div>

      {/* Segments */}
      {statistic.data?.segments?.length > 0 && (
        <div className="statemia-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Segmentaciones</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenModal('segment', 'Editar Segmentación', {
                  dimension: selectedSegment,
                  segment: editedStatistic?.data?.segments?.find(s => s.dimension === selectedSegment)
                })}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit size={16} />
                Editar
              </button>
              <select
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
                className="form-input"
              >
                {statistic.data?.segments?.map((segment) => (
                  <option key={segment.dimension} value={segment.dimension}>
                    {formatSegmentLabel(segment)}
                  </option>
                ))}
              </select>
              
              <div className="flex items-center gap-1 bg-surface-200 rounded-lg p-1">
                {(['bar', 'pie', 'radial'] as ChartType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setChartType(type)}
                    className={`p-2 rounded transition-colors ${
                      chartType === type 
                        ? 'bg-white text-primary shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title={type.charAt(0).toUpperCase() + type.slice(1)}
                  >
                    {getChartTypeIcon(type)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {selectedSegment && (
            <div>
              {statistic.data?.segments
                ?.filter(segment => segment.dimension === selectedSegment)
                .map(segment => (
                  <div key={segment.dimension}>
                    <p className="text-muted-foreground mb-4 whitespace-pre-line">
                      {formatTextWithBreaks(segment.description)}
                    </p>
                    {renderChart(segment.values, segment.dimension)}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Charts */}
      {(editedStatistic?.data?.charts?.length || 0) > 0 && (
        <div className="statemia-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Gráficos Personalizados</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const chartToEdit = editedStatistic?.data?.charts?.find(c => c.id === selectedChart);
                  if (chartToEdit) {
                    handleOpenModal('chart', 'Editar Gráfico', {
                      chartId: selectedChart,
                      chart: JSON.parse(JSON.stringify(chartToEdit)) // Crear copia profunda
                    });
                  }
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit size={16} />
                Editar
              </button>
              <select
                value={selectedChart}
                onChange={(e) => setSelectedChart(e.target.value)}
                className="form-input"
              >
                {editedStatistic?.data?.charts?.map((chart) => (
                  <option key={chart.id} value={chart.id}>
                    {chart.dimension_title || chart.dimension || `Gráfico ${chart.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {selectedChart && (
            <div>
              {editedStatistic?.data?.charts
                ?.filter(chart => chart.id === selectedChart)
                .map(chart => (
                  <div key={chart.id}>
                    <p className="text-muted-foreground mb-4 whitespace-pre-line">
                      {formatTextWithBreaks(chart.description)}
                    </p>
                    {renderCustomChart(chart)}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Evolution */}
      {statistic.data.evolution && renderEvolutionChart()}

      {/* Edit Modal */}
      {modalConfig && (
        <EditModal
          isOpen={modalConfig.isOpen}
          onClose={handleCloseModal}
          onSave={handleModalSave}
          onAskAI={handleAskAI}
          title={modalConfig.title}
          showAIButton={true}
          showUndoButton={true}
          initialData={modalConfig.type === 'chart' ? modalConfig.data.chart : (modalConfig.data.value || modalConfig.data)}
          fullscreen={modalConfig.data.fullscreen || false}
        >
          {modalConfig.type === 'text' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {modalConfig.title}
                </label>
                {modalConfig.data.field === 'description' || modalConfig.data.field === 'studyFlow' ? (
                  <textarea
                    value={modalConfig.data.value}
                    onChange={(e) => {
                      modalConfig.data.value = e.target.value;
                      // Force re-render
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={6}
                    placeholder={`Ingresa el ${modalConfig.data.field === 'description' ? 'descripción' : 'metodología'}...`}
                  />
                ) : (
                  <input
                    type="text"
                    value={modalConfig.data.value}
                    onChange={(e) => {
                      modalConfig.data.value = e.target.value;
                      // Force re-render
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={`Ingresa el ${modalConfig.data.field}...`}
                  />
                )}
              </div>
            </div>
          )}

          {modalConfig.type === 'markdown' && (
            <div className={`${modalConfig.data.fullscreen ? 'h-full flex flex-col' : 'space-y-4'}`}>
              {!modalConfig.data.fullscreen && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {modalConfig.title}
                  </label>
                </div>
              )}
              <div className={modalConfig.data.fullscreen ? 'flex-1 flex flex-col' : ''}>
                <MarkdownEditor
                  value={modalConfig.data.value}
                  onChange={(value) => {
                    modalConfig.data.value = value;
                    setModalConfig({...modalConfig});
                  }}
                  placeholder="Escribe tu descripción con formato Markdown..."
                  charts={modalConfig.data.charts}
                  highlights={modalConfig.data.highlights}
                  segments={modalConfig.data.segments}
                  facts={editedStatistic?.data.blocks || []}
                  amazonProducts={amazonProducts}
                  experiences={experiences}
                  className="w-full"
                  fullscreen={modalConfig.data.fullscreen || false}
                  articleField={modalConfig.data.field || 'description'}
                  statisticTitle={editedStatistic?.title || statistic?.title}
                />
                {!modalConfig.data.fullscreen && (
                  <div className="text-xs text-muted-foreground mt-2">
                    <p><strong>Formato disponible:</strong></p>
                    <p>• <strong>Negrita:</strong> **texto**</p>
                    <p>• <em>Cursiva:</em> *texto*</p>
                    <p>• <a href="#" className="text-primary">Enlaces:</a> [texto](url)</p>
                    <p>• Gráficos: usa el botón 📊 para insertar</p>
                    <p>• Highlights: usa el botón 💡 para insertar highlights destacados</p>
                    <p>• Segmentaciones: usa el botón 📊 para insertar segmentaciones</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {modalConfig.type === 'tags' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tags (separados por comas)
                </label>
                <input
                  type="text"
                  value={modalConfig.data.value}
                  onChange={(e) => {
                    modalConfig.data.value = e.target.value;
                    // Force re-render
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="tag1, tag2, tag3..."
                />
                <p className="text-sm text-muted-foreground mt-2">
                  Separa los tags con comas. Ejemplo: verano, piscinas, España, ocio
                </p>
              </div>
            </div>
          )}

          {modalConfig.type === 'highlights' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Highlights para Insertar en el Contenido
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Instrucciones:</strong> Crea highlights aquí y luego úsalos en la descripción o conclusión con el botón 💡. 
                    Los highlights aparecerán como cajas destacadas dentro del contenido del artículo.
                  </p>
                </div>
                <div className="space-y-3">
                  {modalConfig.data.value.map((highlight: string, index: number) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={highlight}
                        onChange={(e) => {
                          const newHighlights = [...modalConfig.data.value];
                          newHighlights[index] = e.target.value;
                          modalConfig.data.value = newHighlights;
                          setModalConfig({...modalConfig});
                        }}
                        className="flex-1 p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder={`Highlight ${index + 1} - texto breve y destacado`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newHighlights = modalConfig.data.value.filter((_: string, i: number) => i !== index);
                          modalConfig.data.value = newHighlights;
                          setModalConfig({...modalConfig});
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newHighlights = [...modalConfig.data.value, ''];
                      modalConfig.data.value = newHighlights;
                      setModalConfig({...modalConfig});
                    }}
                    className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Añadir highlight
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Los highlights se insertarán en el contenido usando el botón 💡 en los editores de descripción y conclusión.
                </p>
              </div>
            </div>
          )}

          {modalConfig.type === 'related-links' && (
            <RelatedLinksModal
              selectedLinks={modalConfig.data.value}
              onSave={(links) => {
                modalConfig.data.value = links;
                setModalConfig({...modalConfig});
              }}
            />
          )}

          {modalConfig.type === 'block' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Visibilidad en la noticia
                </label>
                <select
                  value={modalConfig.data.block.hidden ? 'hidden' : 'visible'}
                  onChange={(e) => {
                    modalConfig.data.block.hidden = e.target.value === 'hidden';
                    setModalConfig({ ...modalConfig });
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="visible">Visible — aparece en Análisis Detallado</option>
                  <option value="hidden">Oculto — no aparece en Análisis Detallado</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Los bloques ocultos siguen disponibles como facts en descripción y conclusiones
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Título
                </label>
                                  <input
                    type="text"
                    value={modalConfig.data.block.title}
                    onChange={(e) => {
                      modalConfig.data.block.title = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Icono del Bloque
                </label>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => handleOpenSvgIconModal(modalConfig.data.block.id, 'block')}
                    className="p-2 border border-border rounded-lg hover:bg-surface-200 transition-colors flex items-center justify-center min-w-[40px]"
                    title="Seleccionar icono"
                  >
                    {modalConfig.data.block.image_icon ? (
                      <img 
                        src={modalConfig.data.block.image_icon} 
                        alt="Icono del bloque" 
                        className="w-5 h-5 object-contain"
                      />
                    ) : (
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    )}
                  </button>
                  <span className="text-sm text-muted-foreground">
                    {modalConfig.data.block.image_icon ? 'Icono seleccionado' : 'Haz clic para seleccionar un icono'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecciona un icono que represente este bloque de datos
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Valor
                </label>
                                  <input
                    type="number"
                    value={modalConfig.data.block.value || ''}
                    onChange={(e) => {
                      const parsed = parseFloat(e.target.value);
                      modalConfig.data.block.value = isNaN(parsed) ? undefined : parsed;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Unidad/Operación
                </label>
                <select
                  value={modalConfig.data.block.unit || ''}
                  onChange={(e) => {
                    modalConfig.data.block.unit = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Sin unidad</option>
                  <option value="PERCENTAGE">Porcentaje (%)</option>
                  <option value="ADDITION">Suma (+)</option>
                  <option value="SUBTRACTION">Resta (-)</option>
                  <option value="MULTIPLICATION">Multiplicación (×)</option>
                  <option value="DIVISION">División (÷)</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Para bloques de estimación, define cómo se aplica al bloque de entrada
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tipo
                </label>
                                  <select
                    value={modalConfig.data.block.type}
                    onChange={(e) => {
                      modalConfig.data.block.type = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                  <option value="extracted">Extracted (Extraído)</option>
                  <option value="estimation">Estimation (Estimación)</option>
                  <option value="calculated">Calculated (Calculado)</option>
                  <option value="assumption">Assumption (Suposición)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descripción
                </label>
                                  <textarea
                    value={modalConfig.data.block.description}
                    onChange={(e) => {
                      modalConfig.data.block.description = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Fórmula (opcional)
                </label>
                                  <input
                    type="text"
                    value={modalConfig.data.block.formula || ''}
                    onChange={(e) => {
                      modalConfig.data.block.formula = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="P1 * (P2 / 100)"
                  />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Fuente (opcional)
                </label>
                                  <input
                    type="text"
                    value={modalConfig.data.block.source || ''}
                    onChange={(e) => {
                      modalConfig.data.block.source = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="INE (2024)"
                  />
              </div>
            </div>
          )}

          {modalConfig.type === 'segment' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dimensión
                </label>
                <input
                  type="text"
                  value={modalConfig.data.segment.dimension}
                  onChange={(e) => {
                    modalConfig.data.segment.dimension = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="sex_assigned_at_birth"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descripción
                </label>
                <textarea
                  value={modalConfig.data.segment.description}
                  onChange={(e) => {
                    modalConfig.data.segment.description = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Valores (JSON)
                </label>
                <textarea
                  value={JSON.stringify(modalConfig.data.segment.values, null, 2)}
                  onChange={(e) => {
                    try {
                      modalConfig.data.segment.values = JSON.parse(e.target.value);
                      setModalConfig({...modalConfig});
                    } catch (error) {
                      // Invalid JSON, keep the string but don't update
                    }
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  rows={6}
                  placeholder='{"male": 48, "female": 50, "intersex": 2}'
                />
                <p className="text-sm text-muted-foreground mt-2">
                  Formato JSON con pares clave-valor. Ejemplo: {jsonExample}
                </p>
              </div>
            </div>
          )}

          {modalConfig.type === 'chart' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  ID del Bloque Objetivo
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.target_block}
                  onChange={(e) => {
                    modalConfig.data.chart.target_block = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="P7"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  ID del bloque al que se refiere este gráfico (ej: P1, P2, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dimensión
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.dimension}
                  onChange={(e) => {
                    modalConfig.data.chart.dimension = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="age_range"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Título de la Dimensión
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.dimension_title || ''}
                  onChange={(e) => {
                    modalConfig.data.chart.dimension_title = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Rango de edad"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descripción
                </label>
                <textarea
                  value={modalConfig.data.chart.description}
                  onChange={(e) => {
                    modalConfig.data.chart.description = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Título del Eje X
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.xAxisTitle || ''}
                  onChange={(e) => {
                    modalConfig.data.chart.xAxisTitle = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Eje X"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Título del Eje Y
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.yAxisTitle || ''}
                  onChange={(e) => {
                    modalConfig.data.chart.yAxisTitle = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Eje Y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Unidad
                </label>
                <input
                  type="text"
                  value={modalConfig.data.chart.unit || ''}
                  onChange={(e) => {
                    modalConfig.data.chart.unit = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="%, €, kg, etc."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unidad que se mostrará entre paréntesis después de cada valor
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tipo de Gráfico
                </label>
                <select
                  value={modalConfig.data.chart.orientation || 'vertical'}
                  onChange={(e) => {
                    modalConfig.data.chart.orientation = e.target.value as 'vertical' | 'horizontal' | 'radial';
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="vertical">Barras Verticales</option>
                  <option value="horizontal">Barras Horizontales</option>
                  <option value="radial">Gráfico Radial</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecciona el tipo de gráfico a mostrar
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Valores del Gráfico
                </label>
                <div className="space-y-3">
                  {(() => {
                    const values = tempChartValues;
                    // Use custom order if available, otherwise use Object.keys to maintain order
                    const keys = chartKeysOrder.length > 0 ? chartKeysOrder : Object.keys(values);
                    
                    return (
                      <>
                        {keys.map((key, index) => {
                          const value = values[key];
                          if (value === undefined) return null;
                          const icons = modalConfig.data.chart.icons || {};
                          const iconUrl = icons[key];
                          
                          return (
                            <div key={`${key}-${index}`} className="flex gap-2 items-center">
                              <div className="flex-1 flex gap-2">
                                <input
                                  type="text"
                                  value={editingKeys[key] !== undefined ? editingKeys[key] : key}
                                  onChange={(e) => {
                                    setEditingKeys(prev => ({
                                      ...prev,
                                      [key]: e.target.value
                                    }));
                                  }}
                                  onBlur={(e) => {
                                    const newKey = e.target.value.trim();
                                    if (newKey && newKey !== key) {
                                      const newValues = { ...tempChartValues };
                                      delete newValues[key];
                                      newValues[newKey] = value;
                                      setTempChartValues(newValues);
                                      // Update order array
                                      setChartKeysOrder(prev => prev.map(k => k === key ? newKey : k));
                                    }
                                    // Clear editing state
                                    setEditingKeys(prev => {
                                      const newEditingKeys = { ...prev };
                                      delete newEditingKeys[key];
                                      return newEditingKeys;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  className="flex-1 p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                  placeholder="Clave (ej: <5)"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleOpenSvgIconModal(key)}
                                  className="p-2 border border-border rounded-lg hover:bg-surface-200 transition-colors flex items-center justify-center min-w-[40px]"
                                  title="Seleccionar icono"
                                >
                                  {iconUrl ? (
                                    <img 
                                      src={iconUrl} 
                                      alt="Icono seleccionado" 
                                      className="w-5 h-5 object-contain"
                                    />
                                  ) : (
                                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              <input
                                type="number"
                                step="any"
                                value={typeof value === 'number' ? value : (value === '' ? '' : parseFloat(String(value)) || '')}
                                onChange={(e) => {
                                  const newValues = { ...tempChartValues };
                                  const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                  newValues[key] = numValue;
                                  setTempChartValues(newValues);
                                }}
                                className="w-32 p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Valor"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newValues = { ...tempChartValues };
                                  delete newValues[key];
                                  setTempChartValues(newValues);
                                  // Remove from order array
                                  setChartKeysOrder(prev => prev.filter(k => k !== key));
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                X
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => {
                            const newValues = { ...tempChartValues };
                            const newKey = `clave_${Object.keys(newValues).length + 1}`;
                            newValues[newKey] = 0;
                            setTempChartValues(newValues);
                            // Add to order array
                            setChartKeysOrder(prev => [...prev, newKey]);
                          }}
                          className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Añadir valor
                        </button>
                      </>
                    );
                  })()}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Añade pares clave-valor para los datos del gráfico. Ejemplo: clave &quot;&lt;5&quot;, valor &quot;5&quot;
                </p>
              </div>
            </div>
          )}

          {modalConfig.type === 'seo' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meta Title (Título SEO)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={modalConfig.data.metaTitle}
                    onChange={(e) => {
                      modalConfig.data.metaTitle = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="flex-1 p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Piscinas España 2024 - Estadísticas Actualizadas"
                    maxLength={60}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const title = editedStatistic?.title || statistic?.title || '';
                      // Truncar el título si es muy largo para SEO
                      const truncatedTitle = title.length > 60 ? title.substring(0, 57) + '...' : title;
                      modalConfig.data.metaTitle = truncatedTitle;
                      setModalConfig({...modalConfig});
                    }}
                    className="px-3 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                    title="Usar título del artículo (truncado si es necesario)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Título
                  </button>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-muted-foreground">
                    Máximo 60 caracteres. Ideal: 50-60 caracteres
                  </p>
                  <span className={`text-xs ${(modalConfig.data.metaTitle?.length || 0) > 50 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                    {modalConfig.data.metaTitle?.length || 0}/60
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Meta Description (Descripción SEO)
                </label>
                <div className="flex gap-2">
                  <textarea
                    value={modalConfig.data.metaDescription}
                    onChange={(e) => {
                      modalConfig.data.metaDescription = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="flex-1 p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                    placeholder="Descubre cuántas personas se están bañando ahora mismo en piscinas de España. Estadísticas actualizadas con datos demográficos y evolución histórica."
                    maxLength={160}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const lead = editedStatistic?.lead || statistic?.lead || '';
                      // Truncar el lead si es muy largo para SEO
                      const truncatedLead = lead.length > 160 ? lead.substring(0, 157) + '...' : lead;
                      modalConfig.data.metaDescription = truncatedLead;
                      setModalConfig({...modalConfig});
                    }}
                    className="px-3 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 self-start"
                    title="Usar lead del artículo (truncado si es necesario)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Lead
                  </button>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-muted-foreground">
                    Máximo 160 caracteres. Ideal: 150-160 caracteres
                  </p>
                  <span className={`text-xs ${(modalConfig.data.metaDescription?.length || 0) > 150 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                    {modalConfig.data.metaDescription?.length || 0}/160
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Keywords (Palabras Clave)
                </label>
                <input
                  type="text"
                  value={modalConfig.data.keywords}
                  onChange={(e) => {
                    modalConfig.data.keywords = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="piscinas, España, estadísticas, verano, natación, ocio"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Separa las palabras clave con comas
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  OG Image (Imagen para Redes Sociales)
                </label>
                
                {/* Selector de tipo de imagen */}
                <div className="mb-3">
                  <select
                    value={modalConfig.data.ogImageType || 'article'}
                    onChange={(e) => {
                      modalConfig.data.ogImageType = e.target.value;
                      if (e.target.value !== 'upload') {
                        modalConfig.data.ogImage = '';
                        modalConfig.data.ogImageAlt = '';
                        setShowOgImageUploader(false);
                      }
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="article">Imagen del artículo</option>
                    <option value="upload">Subir imagen (1200×630)</option>
                    <option value="custom">URL personalizada</option>
                  </select>
                </div>

                {/* Subir imagen con recorte */}
                {modalConfig.data.ogImageType === 'upload' && (
                  <div className="mb-3">
                    {showOgImageUploader ? (
                      <ImageCropUploader
                        statisticId={editedStatistic?.id}
                        onUploadComplete={(url) => {
                          modalConfig.data.ogImage = url;
                          modalConfig.data.ogImageAlt = editedStatistic?.title || 'Imagen del artículo';
                          setModalConfig({...modalConfig});
                          setShowOgImageUploader(false);
                        }}
                        onCancel={() => setShowOgImageUploader(false)}
                      />
                    ) : (
                      <div className="space-y-3">
                        {/* Preview de imagen actual si existe */}
                        {modalConfig.data.ogImage && (
                          <div className="relative">
                            <img 
                              src={modalConfig.data.ogImage} 
                              alt="OG Image preview" 
                              className="w-full max-w-md h-auto rounded-lg border border-surface-200"
                            />
                            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                              ✓ Imagen subida
                            </div>
                          </div>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => setShowOgImageUploader(true)}
                          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-gray-50 transition-colors flex flex-col items-center gap-2"
                        >
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm font-medium text-foreground">
                            {modalConfig.data.ogImage ? 'Cambiar imagen' : 'Subir imagen OG'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Recorte a 1200×630 • Máximo 10MB
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Campo de URL (para article y custom) */}
                {modalConfig.data.ogImageType !== 'upload' && (
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={modalConfig.data.ogImage}
                      onChange={(e) => {
                        modalConfig.data.ogImage = e.target.value;
                        setModalConfig({...modalConfig});
                      }}
                      className="flex-1 p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                      placeholder="URL de imagen para redes sociales"
                    />
                    {modalConfig.data.ogImageType === 'article' && (
                      <button
                        type="button"
                        onClick={() => {
                          const imageId = editedStatistic?.imageId || statistic?.imageId;
                          if (imageId) {
                            // Si ya tenemos ogImage, usarla directamente
                            if (editedStatistic?.ogImage) {
                              modalConfig.data.ogImage = editedStatistic.ogImage;
                              modalConfig.data.ogImageAlt = editedStatistic.ogImageAlt || generateImageAttribution({ alt: 'Imagen del artículo' }, '');
                              setModalConfig({...modalConfig});
                            } else {
                              // Fallback: generar URL local
                              const baseUrl = window.location.origin;
                              const imageUrl = `${baseUrl}/api/images/serve/${imageId}`;
                              modalConfig.data.ogImage = imageUrl;
                              setModalConfig({...modalConfig});
                            }
                          }
                        }}
                        className="px-3 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                        title="Usar imagen del artículo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Usar Imagen
                      </button>
                    )}
                  </div>
                )}

                {/* Campo OG Image Alt */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    OG Image Alt (Descripción de la imagen)
                  </label>
                  <input
                    type="text"
                    value={modalConfig.data.ogImageAlt || ''}
                    onChange={(e) => {
                      modalConfig.data.ogImageAlt = e.target.value;
                      setModalConfig({...modalConfig});
                    }}
                    className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Descripción de la imagen para accesibilidad"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Descripción de la imagen para lectores de pantalla y accesibilidad
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground mt-1">
                  {modalConfig.data.ogImageType === 'upload' 
                    ? '📷 Sube tu propia imagen. Se recortará a 1200×630 píxeles (formato óptimo para redes sociales) y se guardará en Vercel Blob Storage.'
                    : 'La imagen del artículo se selecciona automáticamente. Para imágenes de stock (Freepik/Pexels) se usa la URL externa, para imágenes propias se usa la URL local.'
                  }
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Canonical URL
                </label>
                <input
                  type="text"
                  value={modalConfig.data.canonicalUrl}
                  onChange={(e) => {
                    modalConfig.data.canonicalUrl = e.target.value;
                    setModalConfig({...modalConfig});
                  }}
                  className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                  placeholder="/statistics/nombre-del-articulo"
                />
                                  <p className="text-xs text-muted-foreground mt-1">
                    URL canónica relativa (ej: /statistics/nombre-articulo). Se auto-genera desde el slug.
                  </p>
              </div>
            </div>
          )}
        </EditModal>
      )}

      {/* Save Preview Modal */}
      <SavePreviewModal
        isOpen={showSavePreview}
        onClose={() => setShowSavePreview(false)}
        onSave={handleSaveToDatabase}
        originalData={statistic}
        editedData={editedStatistic}
        tempImageData={tempImageData}
      />

      {/* Icon Search Modal */}
      <IconSearchModal
        isOpen={svgIconModal.isOpen}
        onClose={handleCloseSvgIconModal}
        onSelect={handleSelectSvgIcon}
        initialQuery={svgIconModal.selectedKey || ''}
      />
    </div>
  );
};

export default StatisticView; 